import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { countryCodes } from './components/countryCodes_full';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';

import * as XLSX from 'xlsx'; // Add this import
import jsPDF from 'jspdf'; // Add this import
import 'jspdf-autotable'; // Add this import

// Google OAuth Configuration
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const API_URL = process.env.REACT_APP_API_URL;

// Main App Component with Routing
function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [cart, setCart] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Load user from localStorage on component mount and fetch data from backend
 useEffect(() => {
  const initializeApp = async () => {
    try {
      const savedUser = localStorage.getItem('sirHenryUser');
      const savedCart = localStorage.getItem('sirHenryCart');
      
      if (savedUser) {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        
        // Verify token with backend
        if (userData.token) {
          try {
            const response = await axios.get(`${API_URL}/index.php`, {
              headers: {
                'Authorization': `Bearer ${userData.token}`,
                'usertypeauth': 'User'
              }
            });
            
            if (response.data.success) {
              setUser(prev => ({ ...prev, ...response.data.user }));
            } else {
              // Token invalid, logout user
              handleLogout();
            }
          } catch (error) {
            console.error('Token verification failed:', error);
            // Keep user logged in but don't update data
          }
        }
        
        // Fetch products AFTER user is set
        await fetchProducts();
      } else {
        // If no user, still fetch products with default role
        await fetchProducts();
      }

      if (savedCart) {
        setCart(JSON.parse(savedCart));
      }

      // Fetch featured products (doesn't depend on user role)
      await fetchFeaturedProducts();

    } catch (error) {
      console.error('Initialization error:', error);
      // Initialize with default products if backend fails
      initializeDefaultProducts();
    } finally {
      setIsLoading(false);
    }
  };

  initializeApp();
}, []);

  const fetchProducts = async () => {
  
  try {
    const currentUser = user || JSON.parse(localStorage.getItem('sirHenryUser') || 'null');
    const token = currentUser?.token;
    //console.log("current User Data = ", currentUser);
    const config = {
      headers: token ? {
        'Authorization': `Bearer ${token}`,
        'usertypeauth': 'User'
      } : {}
    };

    const response = await axios.post(`${API_URL}/index.php`, {
      apiType: "getProducts",
      userRole: currentUser?.role || 'customer',
      userID: currentUser?.id || 0
    }, config);
    
    if (response.data.success) {
      console.log("Get Products Result = ", response.data);
      setProducts(response.data.products);
      localStorage.setItem('sirHenryProducts', JSON.stringify(response.data.products));
    }
  } catch (error) {
    console.error('Error fetching products:', error);
    const savedProducts = localStorage.getItem('sirHenryProducts');
    if (savedProducts) {
      setProducts(JSON.parse(savedProducts));
    }
  }
};

  const fetchFeaturedProducts = async () => {
    try {
      const response = await axios.post(`${API_URL}/index.php`, {
        apiType: "getFeaturedProducts"
      });
      
      if (response.data.success) {
        setFeaturedProducts(response.data.featuredProducts);
        localStorage.setItem('sirHenryFeaturedProducts', JSON.stringify(response.data.featuredProducts));
      }
    } catch (error) {
      console.error('Error fetching featured products:', error);
      // Fallback to localStorage if available
      const savedFeatured = localStorage.getItem('sirHenryFeaturedProducts');
      if (savedFeatured) {
        setFeaturedProducts(JSON.parse(savedFeatured));
      }
    }
  };

  const initializeDefaultProducts = () => {
    const savedProducts = localStorage.getItem('sirHenryProducts');
    const savedFeatured = localStorage.getItem('sirHenryFeaturedProducts');

    if (!savedProducts) {
      const defaultProducts = [
        {
          id: 1,
          name: "Pork Legs",
          image: "https://images.unsplash.com/photo-1550949987-33f716ccc232?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NXx8cG9yayUyMGxlZ3N8ZW58MHx8MHx8fDA%3D&auto=format&fit=crop&w=800&q=60",
          description: "Perfect for roasting and special occasions. Our pork legs are carefully selected for optimal flavor and tenderness.",
          price: 15.99,
          weight: "1kg",
          category: "Premium Cuts",
          isWholesale: false,
          isActive: true
        },
        {
          id: 2,
          name: "Pork Shoulders",
          image: "https://images.unsplash.com/photo-1604503468506-a8da13d82791?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTJ8fHBvcmslMjBzaG91bGRlcnxlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=800&q=60",
          description: "Ideal for slow cooking and pulled pork. These shoulders are perfect for BBQ and family meals.",
          price: 12.99,
          weight: "1kg",
          category: "Premium Cuts",
          isWholesale: false,
          isActive: true
        }
      ];
      setProducts(defaultProducts);
      localStorage.setItem('sirHenryProducts', JSON.stringify(defaultProducts));
    } else {
      setProducts(JSON.parse(savedProducts));
    }

    if (!savedFeatured) {
      const defaultFeatured = [
        {
          id: 1,
          name: "Pork Legs",
          image: "https://images.unsplash.com/photo-1550949987-33f716ccc232?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NXx8cG9yayUyMGxlZ3N8ZW58MHx8MHx8fDA%3D&auto=format&fit=crop&w=800&q=60",
          description: "Perfect for roasting and special occasions"
        },
        {
          id: 2,
          name: "Pork Shoulders",
          image: "https://images.unsplash.com/photo-1594041680534-e8c8cdebd659?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTd8fHBvcmslMjBiZWxseXxlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=800&q=60",
          description: "Ideal for slow cooking and pulled pork"
        }
      ];
      setFeaturedProducts(defaultFeatured);
      localStorage.setItem('sirHenryFeaturedProducts', JSON.stringify(defaultFeatured));
    } else {
      setFeaturedProducts(JSON.parse(savedFeatured));
    }
  };

  // Save cart to localStorage whenever cart changes
  useEffect(() => {
    localStorage.setItem('sirHenryCart', JSON.stringify(cart));
  }, [cart]);

  // Save user to localStorage whenever user changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('sirHenryUser', JSON.stringify(user));
    } else {
      localStorage.removeItem('sirHenryUser');
    }
  }, [user]);

  const addToCart = (product, quantity, purchaseType, customAmount = 0) => {
    setCart(prevCart => {
      const existingItemIndex = prevCart.findIndex(item => 
        item.id === product.id && item.purchaseType === purchaseType
      );
      
      if (existingItemIndex > -1) {
        return prevCart.map((item, index) =>
          index === existingItemIndex
            ? { 
                ...item, 
                quantity: purchaseType === 'amount' ? item.quantity : item.quantity + quantity,
                customAmount: purchaseType === 'amount' ? customAmount : item.customAmount
              }
            : item
        );
      } else {
        return [...prevCart, { 
          ...product, 
          quantity: purchaseType === 'amount' ? 1 : quantity,
          purchaseType,
          customAmount: purchaseType === 'amount' ? customAmount : 0,
          calculatedWeight: purchaseType === 'amount' ? (customAmount / product.price).toFixed(2) : null
        }];
      }
    });
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 0.1) return;
    setCart(prevCart =>
      prevCart.map(item =>
        item.id === productId
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      if (item.purchaseType === 'amount') {
        return total + parseFloat(item.customAmount);
      } else {
        return total + (item.price * item.quantity);
      }
    }, 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => {
      if (item.purchaseType === 'amount') {
        return total + 1;
      } else {
        return total + item.quantity;
      }
    }, 0);
  };

  const handleLogin = (userData) => {
    setUser(userData);
    setCurrentPage('dashboard');
  };

  const handleLogout = () => {
    setUser(null);
    setCart([]);
    setCurrentPage('home');
    localStorage.removeItem('sirHenryUser');
    localStorage.removeItem('sirHenryCart');
  };

  const updateProducts = (newProducts) => {
    setProducts(newProducts);
    localStorage.setItem('sirHenryProducts', JSON.stringify(newProducts));
  };

  const updateFeaturedProducts = (newFeaturedProducts) => {
    setFeaturedProducts(newFeaturedProducts);
    localStorage.setItem('sirHenryFeaturedProducts', JSON.stringify(newFeaturedProducts));
  };

  const renderPage = () => {
    if (isLoading) {
      return <LoadingScreen />;
    }

    switch (currentPage) {
      case 'home':
        return (
          <div>
            <Header 
              isMobile={isMobile} 
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              cartItemCount={getCartItemCount()}
              user={user}
              onLogout={handleLogout}
            />
            <Hero isMobile={isMobile} setCurrentPage={setCurrentPage} />
            <About isMobile={isMobile} />
            <Products isMobile={isMobile} featuredProducts={featuredProducts} />
            <WhyChooseUs isMobile={isMobile} />
            <Testimonials isMobile={isMobile} />
            <Contact isMobile={isMobile} />
            <Footer isMobile={isMobile} setCurrentPage={setCurrentPage} />
          </div>
        );
      case 'shop':
        return (
          <ShopPage 
            isMobile={isMobile}
            addToCart={addToCart}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            cartItemCount={getCartItemCount()}
            user={user}
            onLogout={handleLogout}
            products={products}
          />
        );
      case 'checkout':
        return (
          <CheckoutPage 
            isMobile={isMobile}
            cart={cart}
            removeFromCart={removeFromCart}
            updateQuantity={updateQuantity}
            getCartTotal={getCartTotal}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            clearCart={() => setCart([])}
            user={user}
          />
        );
      case 'login':
        return (
          <LoginPage 
            isMobile={isMobile}
            setCurrentPage={setCurrentPage}
            onLogin={handleLogin}
          />
        );
      case 'signup':
        return (
          <SignupPage 
            isMobile={isMobile}
            setCurrentPage={setCurrentPage}
            onLogin={handleLogin}
          />
        );
      case 'dashboard':
        return (
          <DashboardPage 
            isMobile={isMobile}
            setCurrentPage={setCurrentPage}
            user={user}
            onLogout={handleLogout}
            products={products}
            featuredProducts={featuredProducts}
            updateProducts={updateProducts}
            updateFeaturedProducts={updateFeaturedProducts}
            fetchProducts={fetchProducts}
            fetchFeaturedProducts={fetchFeaturedProducts}
          />
        );
      case 'profile':
        return (
          <ProfilePage 
            isMobile={isMobile}
            setCurrentPage={setCurrentPage}
            user={user}
            onLogout={handleLogout}
            onUserUpdate={setUser}
          />
        );
      case 'change-password':
        return (
          <ChangePasswordPage 
            isMobile={isMobile}
            setCurrentPage={setCurrentPage}
            user={user}
            onLogout={handleLogout}
          />
        );
      default:
        return (
          <div>
            <Header 
              isMobile={isMobile} 
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              cartItemCount={getCartItemCount()}
              user={user}
              onLogout={handleLogout}
            />
            <Hero isMobile={isMobile} setCurrentPage={setCurrentPage} />
            <About isMobile={isMobile} />
            <Products isMobile={isMobile} featuredProducts={featuredProducts} />
            <WhyChooseUs isMobile={isMobile} />
            <Testimonials isMobile={isMobile} />
            <Contact isMobile={isMobile} />
            <Footer isMobile={isMobile} setCurrentPage={setCurrentPage} />
          </div>
        );
    }
  };

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <div style={styles.app}>
        {renderPage()}
      </div>
    </GoogleOAuthProvider>
  );
}

// Loading Screen Component
function LoadingScreen() {
  return (
    <div style={authStyles.loadingScreen}>
      <div style={authStyles.loadingContent}>
        <div style={authStyles.loadingSpinner}></div>
        <h2 style={authStyles.loadingText}>Loading Sir Henry's Butchery...</h2>
      </div>
    </div>
  );
}

// Header Component
function Header({ isMobile, currentPage, setCurrentPage, cartItemCount, user, onLogout }) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header style={styles.header}>
      <div style={styles.container}>
        <div style={styles.logoSection}>
          <h1 
            style={{...styles.logo, cursor: 'pointer'}}
            onClick={() => setCurrentPage('home')}
          >
            Sir Henry's Butchery
          </h1>
          {isMobile && (
            <button 
              style={styles.menuToggle}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              ☰
            </button>
          )}
        </div>
        
        <nav style={{
          ...styles.nav,
          display: isMobile && !menuOpen ? 'none' : 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          position: isMobile ? 'absolute' : 'relative',
          top: isMobile ? '100%' : 'auto',
          left: isMobile ? 0 : 'auto',
          width: isMobile ? '100%' : 'auto',
          backgroundColor: isMobile ? '#1a1a1a' : 'transparent',
          padding: isMobile ? '20px' : 0
        }}>
          <ul style={{
            ...styles.navList,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '15px' : '25px'
          }}>
            <li style={styles.navItem}>
              <a 
                href="#home" 
                style={{
                  ...styles.navLink,
                  color: currentPage === 'home' ? '#d4af37' : 'white'
                }}
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentPage('home');
                  setMenuOpen(false);
                }}
              >
                Home
              </a>
            </li>
            <li style={styles.navItem}>
              <a 
                href="#shop" 
                style={{
                  ...styles.navLink,
                  color: currentPage === 'shop' ? '#d4af37' : 'white'
                }}
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentPage('shop');
                  setMenuOpen(false);
                }}
              >
                Shop
              </a>
            </li>
            <li style={styles.navItem}>
              <a 
                href="#about" 
                style={styles.navLink}
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentPage('home');
                  setMenuOpen(false);
                  setTimeout(() => {
                    document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
              >
                About
              </a>
            </li>
            <li style={styles.navItem}>
              <a 
                href="#products" 
                style={styles.navLink}
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentPage('home');
                  setMenuOpen(false);
                  setTimeout(() => {
                    document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
              >
                Products
              </a>
            </li>
            <li style={styles.navItem}>
              <a 
                href="#testimonials" 
                style={styles.navLink}
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentPage('home');
                  setMenuOpen(false);
                  setTimeout(() => {
                    document.getElementById('testimonials')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
              >
                Reviews
              </a>
            </li>
            <li style={styles.navItem}>
              <a 
                href="#contact" 
                style={styles.navLink}
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentPage('home');
                  setMenuOpen(false);
                  setTimeout(() => {
                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
              >
                Contact
              </a>
            </li>
          </ul>
        </nav>
        
        <div style={styles.headerActions}>
          {!isMobile && (
            <div style={styles.searchContainer}>
              <input type="text" placeholder="Search products..." style={styles.searchInput} />
            </div>
          )}
          
          {/* User Auth Section */}
          <div style={styles.authSection}>
            {user ? (
              <div style={styles.userMenu}>
                <span style={styles.userWelcome}>
                  Welcome, {user.name || user.email}!
                </span>
                <button 
                  style={styles.dashboardButton}
                  onClick={() => setCurrentPage('dashboard')}
                >
                  Dashboard
                </button>
                <button 
                  style={styles.logoutButton}
                  onClick={onLogout}
                >
                  Logout
                </button>
              </div>
            ) : (
              <div style={styles.authButtons}>
                <button 
                  style={styles.loginButton}
                  onClick={() => setCurrentPage('login')}
                >
                  Login
                </button>
                <button 
                  style={styles.signupButton}
                  onClick={() => setCurrentPage('signup')}
                >
                  Sign Up
                </button>
              </div>
            )}
          </div>

          <div 
            style={styles.cartIcon}
            onClick={() => setCurrentPage('checkout')}
          >
            🛒
            {cartItemCount > 0 && (
              <span style={styles.cartBadge}>{cartItemCount}</span>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

// Login Page Component
function LoginPage({ isMobile, setCurrentPage, onLogin }) {
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/index.php`, {
        apiType: "login",
        email: loginData.email,
        password: loginData.password
      });

      if (response.data.success) {
        const userData = {
          ...response.data.user,
          token: response.data.token
        };
        onLogin(userData);
        alert('Login successful!');
      } else {
        alert(response.data.message || 'Login failed. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      
      const response = await axios.post(`${API_URL}/index.php`, {
        apiType: "googleSignIn",
        googleId: decoded.sub,
        email: decoded.email,
        name: decoded.name,
        imageUrl: decoded.picture
      });

      if (response.data.success) {
        const userData = {
          ...response.data.user,
          token: response.data.token
        };
        onLogin(userData);
        console.log("Response = ", response.data);
        alert('Google login successful!');
      } else {
        alert('Google login failed. Please try again.');
      }
    } catch (error) {
      console.error('Google login error:', error);
      alert('Google login failed. Please try again.');
    }
  };

  return (
    <div style={authStyles.page}>
      <div style={authStyles.container}>
        <div style={authStyles.card}>
          <div style={authStyles.header}>
            <h1 style={authStyles.title}>Welcome Back</h1>
            <p style={authStyles.subtitle}>Sign in to your Sir Henry's account</p>
          </div>

          <form onSubmit={handleSubmit} style={authStyles.form}>
            <div style={authStyles.formGroup}>
              <label style={authStyles.label}>Email Address</label>
              <input
                type="email"
                name="email"
                value={loginData.email}
                onChange={handleInputChange}
                required
                style={authStyles.input}
                placeholder="Enter your email"
              />
            </div>

            <div style={authStyles.formGroup}>
              <label style={authStyles.label}>Password</label>
              <input
                type="password"
                name="password"
                value={loginData.password}
                onChange={handleInputChange}
                required
                style={authStyles.input}
                placeholder="Enter your password"
              />
            </div>

            <button 
              type="submit" 
              style={authStyles.submitButton}
              disabled={isLoading}
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          <div style={authStyles.divider}>
            <span style={authStyles.dividerText}>OR</span>
          </div>

          <div style={authStyles.googleSection}>
            <GoogleLogin
              onSuccess={handleGoogleLogin}
              onError={() => {
                alert('Google login failed. Please try again.');
              }}
              useOneTap={false}
            />
          </div>

          <div style={authStyles.footer}>
            <p style={authStyles.footerText}>
              Don't have an account?{' '}
              <button 
                style={authStyles.linkButton}
                onClick={() => setCurrentPage('signup')}
              >
                Sign up here
              </button>
            </p>
            <button 
              style={authStyles.backButton}
              onClick={() => setCurrentPage('home')}
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Signup Page Component
function SignupPage({ isMobile, setCurrentPage, onLogin }) {
  const [signupData, setSignupData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+263',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSignupData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (signupData.password !== signupData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    if (!signupData.acceptTerms) {
      alert('Please accept the terms and conditions');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/index.php`, {
        apiType: "signUp",
        name: signupData.name,
        email: signupData.email,
        phone: signupData.phone,
        countryCode: signupData.countryCode,
        password: signupData.password
      });

      if (response.data.success) {
        const userData = {
          ...response.data.user,
          token: response.data.token
        };
        onLogin(userData);
        alert('Account created successfully! Welcome to Sir Henry\'s Butchery.');
      } else {
        alert(response.data.message || 'Signup failed. Please try again.');
      }
    } catch (error) {
      console.error('Signup error:', error);
      alert('Signup failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      
      const response = await axios.post(`${API_URL}/index.php`, {
        apiType: "googleSignIn",
        googleId: decoded.sub,
        email: decoded.email,
        name: decoded.name,
        imageUrl: decoded.picture
      });

      if (response.data.success) {
        const userData = {
          ...response.data.user,
          token: response.data.token
        };
        onLogin(userData);
        alert('Google signup successful!');
      } else {
        alert('Google signup failed. Please try again.');
      }
    } catch (error) {
      console.error('Google signup error:', error);
      alert('Google signup failed. Please try again.');
    }
  };

  return (
    <div style={authStyles.page}>
      <div style={authStyles.container}>
        <div style={authStyles.card}>
          <div style={authStyles.header}>
            <h1 style={authStyles.title}>Create Account</h1>
            <p style={authStyles.subtitle}>Join Sir Henry's Butchery family</p>
          </div>

          <form onSubmit={handleSubmit} style={authStyles.form}>
            <div style={authStyles.formGroup}>
              <label style={authStyles.label}>Full Name</label>
              <input
                type="text"
                name="name"
                value={signupData.name}
                onChange={handleInputChange}
                required
                style={authStyles.input}
                placeholder="Enter your full name"
              />
            </div>

            <div style={authStyles.formGroup}>
              <label style={authStyles.label}>Email Address</label>
              <input
                type="email"
                name="email"
                value={signupData.email}
                onChange={handleInputChange}
                required
                style={authStyles.input}
                placeholder="Enter your email"
              />
            </div>

            <div style={authStyles.formGroup}>
              <label style={authStyles.label}>Phone Number</label>
              <div style={authStyles.phoneInputContainer}>
                <select 
                  name="countryCode"
                  value={signupData.countryCode}
                  onChange={handleInputChange}
                  style={authStyles.countryCodeSelect}
                >
                  {countryCodes.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.flag} {country.code}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  name="phone"
                  value={signupData.phone}
                  onChange={handleInputChange}
                  style={authStyles.phoneInput}
                  placeholder="Phone number"
                />
              </div>
            </div>

            <div style={authStyles.formGroup}>
              <label style={authStyles.label}>Password</label>
              <input
                type="password"
                name="password"
                value={signupData.password}
                onChange={handleInputChange}
                required
                style={authStyles.input}
                placeholder="Create a password"
                minLength="6"
              />
            </div>

            <div style={authStyles.formGroup}>
              <label style={authStyles.label}>Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                value={signupData.confirmPassword}
                onChange={handleInputChange}
                required
                style={authStyles.input}
                placeholder="Confirm your password"
                minLength="6"
              />
            </div>

            <div style={authStyles.checkboxGroup}>
              <label style={authStyles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={signupData.acceptTerms}
                  onChange={handleInputChange}
                  style={authStyles.checkbox}
                />
                I agree to the{' '}
                <button 
                  type="button"
                  style={authStyles.linkButton}
                  onClick={() => alert('Terms and conditions will be displayed here')}
                >
                  Terms and Conditions
                </button>
              </label>
            </div>

            <button 
              type="submit" 
              style={authStyles.submitButton}
              disabled={isLoading}
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          <div style={authStyles.divider}>
            <span style={authStyles.dividerText}>OR</span>
          </div>

          <div style={authStyles.googleSection}>
            <GoogleLogin
              onSuccess={handleGoogleLogin}
              onError={() => {
                alert('Google signup failed. Please try again.');
              }}
              useOneTap={false}
            />
          </div>

          <div style={authStyles.footer}>
            <p style={authStyles.footerText}>
              Already have an account?{' '}
              <button 
                style={authStyles.linkButton}
                onClick={() => setCurrentPage('login')}
              >
                Sign in here
              </button>
            </p>
            <button 
              style={authStyles.backButton}
              onClick={() => setCurrentPage('home')}
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Dashboard Page Component
function DashboardPage({ isMobile, setCurrentPage, user, onLogout, products, featuredProducts, updateProducts, updateFeaturedProducts, fetchProducts, fetchFeaturedProducts }) {
  const [activeTab, setActiveTab] = useState('overview');

  const staffTabs = [
    { id: 'overview', name: 'Overview', icon: '📊' },
    { id: 'products', name: 'Product Management', icon: '🍖' },
    { id: 'featured', name: 'Featured Products', icon: '⭐' },
    { id: 'orders', name: 'Order Management', icon: '📦' },
    { id: 'users', name: 'User Management', icon: '👥' },
    { id: 'sales', name: 'Sales Report', icon: '📈' },
    { id: 'profile', name: 'My Profile', icon: '👤' }
  ];

  const customerTabs = [
    { id: 'overview', name: 'Overview', icon: '📊' },
    { id: 'orders', name: 'My Orders', icon: '📦' },
    { id: 'profile', name: 'My Profile', icon: '👤' },
    { id: 'change-password', name: 'Change Password', icon: '🔒' }
  ];

  const tabs = user?.role === 'staff' ? staffTabs : customerTabs;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <DashboardOverview user={user} isMobile={isMobile} setCurrentPage={setCurrentPage} />;
      case 'products':
        return user?.role === 'staff' ? 
          <ProductManagement 
            products={products} 
            updateProducts={updateProducts}
            fetchProducts={fetchProducts}
            isMobile={isMobile} 
          /> : null;
      case 'featured':
        return user?.role === 'staff' ? 
          <FeaturedProductsManagement 
            products={products}
            featuredProducts={featuredProducts} 
            updateFeaturedProducts={updateFeaturedProducts}
            fetchFeaturedProducts={fetchFeaturedProducts}
            isMobile={isMobile} 
          /> : null;
     /* case 'orders':
        return user?.role === 'staff' ? 
          <OrderManagement isMobile={isMobile} user={user} /> : 
          <CustomerOrders isMobile={isMobile} user={user} />;*/
          case 'orders':
  return user?.role === 'staff' ? 
    <OrderManagement isMobile={isMobile} user={user} /> : 
    <CustomerOrders isMobile={isMobile} user={user} setCurrentPage={setCurrentPage} />;
      case 'users':
        return user?.role === 'staff' ? <UserManagement isMobile={isMobile} /> : null;
      case 'sales':
        return user?.role === 'staff' ? <SalesReport isMobile={isMobile} /> : null;
      case 'profile':
        return <ProfileSection user={user} setCurrentPage={setCurrentPage} isMobile={isMobile} />;
      case 'change-password':
        return <ChangePasswordSection setCurrentPage={setCurrentPage} isMobile={isMobile} user={user} />;
      default:
        return <DashboardOverview user={user} isMobile={isMobile} setCurrentPage={setCurrentPage} />;
    }
  };

  return (
    <div style={dashboardStyles.page}>
      <Header 
        isMobile={isMobile} 
        currentPage="dashboard"
        setCurrentPage={setCurrentPage}
        cartItemCount={0}
        user={user}
        onLogout={onLogout}
      />
      
      <div style={dashboardStyles.container}>
        <div style={dashboardStyles.header}>
          <h1 style={dashboardStyles.title}>
            {user?.role === 'staff' ? 'Staff Dashboard' : 'My Account'}
          </h1>
          <p style={dashboardStyles.subtitle}>
            Welcome back, {user?.name || user?.email}! {user?.role === 'staff' ? 'Manage your butchery efficiently.' : 'Track your orders and manage your account.'}
          </p>
        </div>

        <div style={{
          ...dashboardStyles.content,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <div style={dashboardStyles.sidebar}>
            <nav style={dashboardStyles.nav}>
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  style={{
                    ...dashboardStyles.navButton,
                    ...(activeTab === tab.id ? dashboardStyles.navButtonActive : {})
                  }}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span style={dashboardStyles.navIcon}>{tab.icon}</span>
                  <span style={dashboardStyles.navText}>{tab.name}</span>
                </button>
              ))}
            </nav>
            
            <div style={dashboardStyles.sidebarFooter}>
              <p style={dashboardStyles.userRole}>
                Role: <strong>{user?.role === 'staff' ? 'Staff Member' : 'Customer'}</strong>
              </p>
              <button 
                style={dashboardStyles.backToShop}
                onClick={() => setCurrentPage('shop')}
              >
                ← Back to Shop
              </button>
            </div>
          </div>

          <div style={dashboardStyles.mainContent}>
            {renderTabContent()}
          </div>
        </div>
      </div>

      <Footer isMobile={isMobile} setCurrentPage={setCurrentPage} />
    </div>
  );
}

// Dashboard Overview Component
function DashboardOverview({ user, isMobile, setCurrentPage }) {
  const [stats, setStats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.post(`${API_URL}/index.php`, {
          apiType: "getDashboardStats"
        }, {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'usertypeauth': 'User'
          }
        });

        if (response.data.success) {
          //console.log("Stats Data = ", response.data);
          // Map the action strings to actual functions
          const statsWithActions = response.data.stats.map(stat => ({
            ...stat,
            action: () => setCurrentPage(stat.action)
          }));
          setStats(statsWithActions);
        }
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        // Fallback to mock data if API fails
        setMockStats();
      } finally {
        setIsLoading(false);
      }
    };

    const setMockStats = () => {
      // Fallback to your original mock data
      if (user?.role === 'staff') {
        setStats([
          { label: 'Total Products', value: '24', change: '+5%', action: () => setCurrentPage('products') },
          { label: 'Orders Today', value: '18', change: '+12%', action: () => setCurrentPage('orders') },
          { label: 'Revenue', value: '$1,240', change: '+8%', action: () => setCurrentPage('sales') },
          { label: 'Customers', value: '156', change: '+3%', action: () => setCurrentPage('users') }
        ]);
      } else {
        setStats([
          { label: 'Total Orders', value: '12', change: '+2', action: () => setCurrentPage('orders') },
          { label: 'Pending Orders', value: '2', change: '-1', action: () => setCurrentPage('orders') },
          { label: 'Completed Orders', value: '10', change: '+3', action: () => setCurrentPage('orders') },
          { label: 'Total Spent', value: '$480', change: '+$120', action: () => setCurrentPage('orders') }
        ]);
      }
    };

    if (user?.token) {
      fetchStats();
    } else {
      setMockStats();
      setIsLoading(false);
    }
  }, [user, setCurrentPage]);

  if (isLoading) {
    return (
      <div style={dashboardStyles.overview}>
        <h2 style={dashboardStyles.sectionTitle}>Overview</h2>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={authStyles.loadingSpinner}></div>
          <p>Loading statistics...</p>
        </div>
      </div>
    );
  }

  const handleQuickAction = (action) => {
    switch (action) {
      case 'addProduct':
        setCurrentPage('products');
        break;
      case 'salesReport':
        setCurrentPage('sales');
        break;
      case 'viewUsers':
        setCurrentPage('users');
        break;
      case 'continueShopping':
        setCurrentPage('shop');
        break;
      case 'contactSupport':
        // Scroll to contact section on home page
        setCurrentPage('home');
        setTimeout(() => {
          document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
        break;
      case 'viewOrders':
        setCurrentPage('orders');
        break;
      default:
        break;
    }
  };

  return (
    <div style={dashboardStyles.overview}>
      <h2 style={dashboardStyles.sectionTitle}>Overview</h2>
      
      <div style={{
        ...dashboardStyles.statsGrid,
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)'
      }}>
        {stats.map((stat, index) => (
          <div 
            key={index} 
            style={dashboardStyles.statCard}
            onClick={stat.action}
          >
            <h3 style={dashboardStyles.statValue}>{stat.value}</h3>
            <p style={dashboardStyles.statLabel}>{stat.label}</p>
            <span style={dashboardStyles.statChange}>{stat.change}</span>
          </div>
        ))}
      </div>

      <div style={dashboardStyles.section}>
        <h3 style={dashboardStyles.sectionTitle}>Recent Activity</h3>
        <div style={dashboardStyles.activityList}>
          {user?.role === 'staff' ? (
            <>
              <div style={dashboardStyles.activityItem}>
                <span style={dashboardStyles.activityIcon}>📦</span>
                <div style={dashboardStyles.activityContent}>
                  <p style={dashboardStyles.activityText}>New order #1234 received</p>
                  <span style={dashboardStyles.activityTime}>2 hours ago</span>
                </div>
              </div>
              <div style={dashboardStyles.activityItem}>
                <span style={dashboardStyles.activityIcon}>🍖</span>
                <div style={dashboardStyles.activityContent}>
                  <p style={dashboardStyles.activityText}>Product "Pork Loin" updated</p>
                  <span style={dashboardStyles.activityTime}>4 hours ago</span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div style={dashboardStyles.activityItem}>
                <span style={dashboardStyles.activityIcon}>📦</span>
                <div style={dashboardStyles.activityContent}>
                  <p style={dashboardStyles.activityText}>Order #1234 delivered successfully</p>
                  <span style={dashboardStyles.activityTime}>2 days ago</span>
                </div>
              </div>
              <div style={dashboardStyles.activityItem}>
                <span style={dashboardStyles.activityIcon}>🛒</span>
                <div style={dashboardStyles.activityContent}>
                  <p style={dashboardStyles.activityText}>New order #1235 placed</p>
                  <span style={dashboardStyles.activityTime}>3 days ago</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div style={dashboardStyles.section}>
        <h3 style={dashboardStyles.sectionTitle}>Quick Actions</h3>
        <div style={dashboardStyles.actionsGrid}>
          {user?.role === 'staff' ? (
            <>
              <button 
                style={dashboardStyles.actionButton}
                onClick={() => handleQuickAction('addProduct')}
              >
                <span style={dashboardStyles.actionIcon}>➕</span>
                Add New Product
              </button>
              <button 
                style={dashboardStyles.actionButton}
                onClick={() => handleQuickAction('salesReport')}
              >
                <span style={dashboardStyles.actionIcon}>📊</span>
                View Sales Report
              </button>
              <button 
                style={dashboardStyles.actionButton}
                onClick={() => handleQuickAction('viewUsers')}
              >
                <span style={dashboardStyles.actionIcon}>👥</span>
                Manage Users
              </button>
              <button 
                style={dashboardStyles.actionButton}
                onClick={() => setCurrentPage('orders')}
              >
                <span style={dashboardStyles.actionIcon}>📦</span>
                View Orders
              </button>
            </>
          ) : (
            <>
              <button 
                style={dashboardStyles.actionButton}
                onClick={() => handleQuickAction('continueShopping')}
              >
                <span style={dashboardStyles.actionIcon}>🛒</span>
                Continue Shopping
              </button>
              <button 
                style={dashboardStyles.actionButton}
                onClick={() => handleQuickAction('viewOrders')}
              >
                <span style={dashboardStyles.actionIcon}>📦</span>
                View My Orders
              </button>
              <button 
                style={dashboardStyles.actionButton}
                onClick={() => handleQuickAction('contactSupport')}
              >
                <span style={dashboardStyles.actionIcon}>📞</span>
                Contact Support
              </button>
              <button 
                style={dashboardStyles.actionButton}
                onClick={() => setCurrentPage('profile')}
              >
                <span style={dashboardStyles.actionIcon}>👤</span>
                Update Profile
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Product Management Component (Staff Only)
function ProductManagement({ products, updateProducts, fetchProducts, isMobile }) {
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [viewMode, setViewMode] = useState('tabular'); // 'tabular' or 'linear'
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    weight: '',
    category: 'Premium Cuts',
    image: '',
    isWholesale: false,
    isActive: true,
    id: 0
  });
  const [imageInputType, setImageInputType] = useState('url');
  const [imageFile, setImageFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const categories = ['Premium Cuts', 'BBQ Specials', 'Traditional Cuts', 'Wholesale'];

  
  const filteredProducts = products.filter(product => {
    return Object.keys(filters).every(key => {
      if (!filters[key]) return true;
      return String(product[key]).toLowerCase().includes(filters[key].toLowerCase());
    });
  });

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProductForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Please select a valid image file (JPEG, PNG, GIF, WebP)');
        e.target.value = '';
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        e.target.value = '';
        return;
      }
      
      setImageFile(file);
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setProductForm(prev => ({
        ...prev,
        image: previewUrl
      }));
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    
    try {
      let finalImageUrl = productForm.image;

      // If uploading a file, we need to send as FormData
      if (imageInputType === 'upload' && imageFile) {
        setIsUploading(true);
        
        // Create FormData for file upload
        const formData = new FormData();
        formData.append('image', imageFile);
        formData.append('apiType', 'submitProduct');
        formData.append('name', productForm.name);
        formData.append('description', productForm.description);
        formData.append('price', parseFloat(productForm.price));
        formData.append('weight', productForm.weight);
        formData.append('category', productForm.category);
        formData.append('isWholesale', productForm.isWholesale ? 1 : 0);
        formData.append('isActive', 1);

        const response = await axios.post(
          `${API_URL}/index.php`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress(percentCompleted);
            }
          }
        );

        if (response.data.success) {
          await fetchProducts();
          setIsAddingProduct(false);
          setProductForm({
            name: '',
            description: '',
            price: '',
            weight: '',
            category: 'Premium Cuts',
            image: '',
            isWholesale: false,
            isActive: true
          });
          setImageFile(null);
          setImageInputType('url');
          setUploadProgress(0);
          alert('Product added successfully!');
        } else {
          alert('Failed to add product. Please try again.');
        }
      } else {
        // If using URL, send as JSON
        const response = await axios.post(
          `${API_URL}/index.php`,
          {
            apiType: "submitProduct",
            name: productForm.name,
            description: productForm.description,
            price: parseFloat(productForm.price),
            weight: productForm.weight,
            category: productForm.category,
            image: finalImageUrl,
            isWholesale: productForm.isWholesale ? 1 : 0,
            isActive: 1
          }
        );

        if (response.data.success) {
          await fetchProducts();
          setIsAddingProduct(false);
          setProductForm({
            name: '',
            description: '',
            price: '',
            weight: '',
            category: 'Premium Cuts',
            image: '',
            isWholesale: false,
            isActive: true
          });
          setImageFile(null);
          setImageInputType('url');
          setUploadProgress(0);
          alert('Product added successfully!');
        } else {
          alert('Failed to add product. Please try again.');
        }
      }
    } catch (error) {
      console.error("Error posting Product:", error);
      alert("Error adding product. Check your internet connection.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price,
      weight: product.weight,
      category: product.category,
      image: product.image,
      isWholesale: product.isWholesale,
      isActive: product.isActive,
      id: product.id
    });
    // For editing, default to URL input type
    setImageInputType('url');
    setImageFile(null);
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    
    try {
      let finalImageUrl = productForm.image;

      // If uploading a file, we need to send as FormData
      if (imageInputType === 'upload' && imageFile) {
        setIsUploading(true);
        
        // Create FormData for file upload
        const formData = new FormData();
        formData.append('image', imageFile);
        formData.append('apiType', 'submitEditProduct');
        formData.append('name', productForm.name);
        formData.append('description', productForm.description);
        formData.append('price', parseFloat(productForm.price));
        formData.append('weight', productForm.weight);
        formData.append('category', productForm.category);
        formData.append('isWholesale', productForm.isWholesale ? 1 : 0);
        formData.append('isActive', productForm.isActive ? 1 : 0);
        formData.append('id', productForm.id);

        const response = await axios.post(
          `${API_URL}/index.php`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress(percentCompleted);
            }
          }
        );

        if (response.data.success) {
          await fetchProducts();
          setEditingProduct(null);
          setProductForm({
            name: '',
            description: '',
            price: '',
            weight: '',
            category: 'Premium Cuts',
            image: '',
            isWholesale: false,
            isActive: true
          });
          setImageFile(null);
          setImageInputType('url');
          setUploadProgress(0);
          alert('Product updated successfully!');
        } else {
          alert('Failed to update product. Please try again.');
        }
      } else {
        // If using URL, send as JSON
        const response = await axios.post(
          `${API_URL}/index.php`,
          {
            apiType: "submitEditProduct",
            name: productForm.name,
            description: productForm.description,
            price: parseFloat(productForm.price),
            weight: productForm.weight,
            category: productForm.category,
            image: finalImageUrl,
            isWholesale: productForm.isWholesale ? 1 : 0,
            isActive: productForm.isActive ? 1 : 0,
            id: productForm.id
          }
        );

        if (response.data.success) {
          await fetchProducts();
          setEditingProduct(null);
          setProductForm({
            name: '',
            description: '',
            price: '',
            weight: '',
            category: 'Premium Cuts',
            image: '',
            isWholesale: false,
            isActive: true
          });
          setImageFile(null);
          setImageInputType('url');
          setUploadProgress(0);
          alert('Product updated successfully!');
        } else {
          alert('Failed to update product. Please try again.');
        }
      }
    } catch (error) {
      console.error("Error updating product:", error);
      alert("Error updating product. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

 const handleDeactivate = async (productId, activeStatus) => {
  try {
    // Normalize and validate the input
    const normalizedStatus = activeStatus.toLowerCase();

    // Determine API action and message
    const apiType =
      normalizedStatus === "activate" ? "activateProduct" :
      normalizedStatus === "deactivate" ? "deactivateProduct" :
      null;

    if (!apiType) {
      alert("Invalid action type provided.");
      return;
    }

    const actionMessage = normalizedStatus === "activate" ? "activated" : "deactivated";

    const response = await axios.post(`${API_URL}/index.php`, {
      apiType,
      productId,
      activeStatus: normalizedStatus
    });

    if (response.data.success) {
      await fetchProducts();
      alert(`Product ${actionMessage} successfully!`);
    } else {
      alert(`Failed to ${actionMessage} product.`);
    }
  } catch (error) {
    console.error("Deactivate error:", error);
    alert(`Failed to ${activeStatus === "activate" ? "activate" : "deactivate"} product. Check your network connection.`);
  }
};



  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      try {
        const response = await axios.post(`${API_URL}/index.php`, {
          apiType: "deleteProduct",
          productId: productId
        });

        if (response.data.success) {
          await fetchProducts();
          alert('Product deleted successfully!');
        } else {
          alert('Failed to delete product.');
        }
      } catch (error) {
        console.error('Delete error:', error);
        alert('Failed to delete product.');
      }
    }
  };

  const handleExport = async (exportType) => {
    try {
      // Get filtered data from the current view
      const exportData = filteredProducts.map((product, index) => ({
        'SN': index + 1,
        'Name': product.name,
        'Description': product.description,
        'Price': `$${product.price}`,
        'Weight': product.weight,
        'Category': product.category,
        'Wholesale': product.isWholesale ? 'Yes' : 'No',
        'Status': product.isActive ? 'Active' : 'Inactive'
      }));

      if (exportType === 'excel') {
        // Using SheetJS library from CDN for Excel export
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Products");
        XLSX.writeFile(wb, `products_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      } else if (exportType === 'pdf') {
        // Using jsPDF and autoTable from CDN for PDF export
        const { jsPDF } = window.jspdf;
        const doc = new jsPDF();
        
        // Add title
        doc.setFontSize(16);
        doc.text('Products Export', 14, 15);
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 22);
        
        // Prepare data for autoTable
        const tableData = exportData.map(item => [
          item.SN,
          item.Name,
          item.Description.substring(0, 50) + (item.Description.length > 50 ? '...' : ''),
          item.Price,
          item.Weight,
          item.Category,
          item.Wholesale,
          item.Status
        ]);

        // Add table using autoTable
        doc.autoTable({
          startY: 25,
          head: [['SN', 'Name', 'Description', 'Price', 'Weight', 'Category', 'Wholesale', 'Status']],
          body: tableData,
          theme: 'grid',
          styles: { fontSize: 8 },
          headStyles: { fillColor: [26, 26, 26] },
          margin: { top: 25 }
        });

        doc.save(`products_export_${new Date().toISOString().split('T')[0]}.pdf`);
      }
      
      alert(`Products exported successfully as ${exportType.toUpperCase()}!`);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export products. Make sure the export libraries are loaded.');
    }
  };

  const resetImageInput = () => {
    setImageFile(null);
    setProductForm(prev => ({
      ...prev,
      image: ''
    }));
  };

  const resetForm = () => {
    setIsAddingProduct(false);
    setEditingProduct(null);
    setProductForm({
      name: '',
      description: '',
      price: '',
      weight: '',
      category: 'Premium Cuts',
      image: '',
      isWholesale: false,
      isActive: true
    });
    setImageFile(null);
    setImageInputType('url');
    setUploadProgress(0);
  };

  return (
    <div style={dashboardStyles.section}>
      <div style={dashboardStyles.sectionHeader}>
        <h2 style={dashboardStyles.sectionTitle}>Product Management</h2>
        <div style={dashboardStyles.headerActions}>
          <button 
            style={dashboardStyles.addButton}
            onClick={() => setIsAddingProduct(true)}
          >
            + Add New Product
          </button>
          <select 
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            style={dashboardStyles.viewModeSelect}
          >
            <option value="tabular">Tabular View</option>
            <option value="linear">Linear View</option>
          </select>
        </div>
      </div>

      {/* Filters */}
      <div style={dashboardStyles.filtersContainer}>
        <div style={dashboardStyles.filterRow}>
          <input
            type="text"
            placeholder="Filter by name..."
            value={filters.name || ''}
            onChange={(e) => handleFilterChange('name', e.target.value)}
            style={dashboardStyles.filterInput}
          />
          <input
            type="text"
            placeholder="Filter by category..."
            value={filters.category || ''}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            style={dashboardStyles.filterInput}
          />
          <input
            type="text"
            placeholder="Filter by price..."
            value={filters.price || ''}
            onChange={(e) => handleFilterChange('price', e.target.value)}
            style={dashboardStyles.filterInput}
          />
          <input
            type="text"
            placeholder="Filter by status..."
            value={filters.isActive || ''}
            onChange={(e) => handleFilterChange('isActive', e.target.value)}
            style={dashboardStyles.filterInput}
          />
        </div>
        
        {/* Export Buttons */}
        <div style={dashboardStyles.exportButtons}>
          <button 
            style={dashboardStyles.exportButton}
            onClick={() => handleExport('excel')}
          >
            📊 Export Excel
          </button>
          <button 
            style={dashboardStyles.exportButton}
            onClick={() => handleExport('pdf')}
          >
            📄 Export PDF
          </button>
        </div>
      </div>

      {/* Products Table */}
      {viewMode === 'tabular' ? (
        <div style={dashboardStyles.tableContainer}>
          <table style={dashboardStyles.table}>
            <thead>
              <tr>
                <th style={dashboardStyles.tableHeader}>SN</th>
                <th style={dashboardStyles.tableHeader}>Image</th>
                <th style={dashboardStyles.tableHeader}>Name</th>
                <th style={dashboardStyles.tableHeader}>Description</th>
                <th style={dashboardStyles.tableHeader}>Price</th>
                <th style={dashboardStyles.tableHeader}>Weight</th>
                <th style={dashboardStyles.tableHeader}>Category</th>
                <th style={dashboardStyles.tableHeader}>Wholesale</th>
                <th style={dashboardStyles.tableHeader}>Status</th>
                <th style={dashboardStyles.tableHeader}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedProducts.map((product, index) => (
                <tr key={product.id} style={dashboardStyles.tableRow}>
                  <td style={dashboardStyles.tableCell}>
                    {(currentPage - 1) * itemsPerPage + index + 1}
                  </td>
                  <td style={dashboardStyles.tableCell}>
                    <img 
                      src={product.image} 
                      alt={product.name}
                      style={dashboardStyles.tableImage}
                    />
                  </td>
                  <td style={dashboardStyles.tableCell}>{product.name}</td>
                  <td style={dashboardStyles.tableCell}>
                    {product.description.substring(0, 50)}...
                  </td>
                  <td style={dashboardStyles.tableCell}>${product.price}</td>
                  <td style={dashboardStyles.tableCell}>{product.weight}</td>
                  <td style={dashboardStyles.tableCell}>{product.category}</td>
                  <td style={dashboardStyles.tableCell}>
                    {product.isWholesale ? 'Yes' : 'No'}
                  </td>
                  <td style={dashboardStyles.tableCell}>
                    <span style={{
                      ...dashboardStyles.statusBadge,
                      backgroundColor: product.isActive ? '#4CAF50' : '#ff9800'
                    }}>
                      {product.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={dashboardStyles.tableCell}>
                    <div style={dashboardStyles.actionButtons}>
                      <button 
                        style={dashboardStyles.editButton}
                        onClick={() => handleEditProduct(product)}
                      >
                        Edit
                      </button>
                      <button 
                        style={dashboardStyles.toggleButton}
                        onClick={() => handleDeactivate(product.id, product.isActive ? 'Deactivate' : 'Activate')}
                      >
                        {product.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button 
                        style={dashboardStyles.deleteButton}
                        onClick={() => handleDelete(product.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div style={dashboardStyles.pagination}>
            <div style={dashboardStyles.paginationInfo}>
              Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredProducts.length)} of {filteredProducts.length} products
            </div>
            <div style={dashboardStyles.paginationControls}>
              <button 
                style={dashboardStyles.paginationButton}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
              >
                Previous
              </button>
              <span style={dashboardStyles.paginationNumbers}>
                Page {currentPage} of {totalPages}
              </span>
              <button 
                style={dashboardStyles.paginationButton}
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => prev + 1)}
              >
                Next
              </button>
            </div>
            <div style={dashboardStyles.pageSizeSelector}>
              <label>Rows per page: </label>
              <select 
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={dashboardStyles.pageSizeSelect}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
            </div>
          </div>
        </div>
      ) : (
        // Linear View (existing card view)
        <div style={dashboardStyles.productsList}>
          {paginatedProducts.map((product, index) => (
            <div key={product.id} style={{
              ...dashboardStyles.productItem,
              opacity: product.isActive ? 1 : 0.6
            }}>
              <img 
                src={product.image} 
                alt={product.name} 
                style={dashboardStyles.productImage}
              />
              <div style={dashboardStyles.productInfo}>
                <h4 style={dashboardStyles.productName}>{product.name}</h4>
                <p style={dashboardStyles.productDescription}>{product.description}</p>
                <div style={dashboardStyles.productDetails}>
                  <span style={dashboardStyles.productPrice}>${product.price}/kg</span>
                  <span style={dashboardStyles.productWeight}>{product.weight}</span>
                  <span style={dashboardStyles.productCategory}>{product.category}</span>
                  <span style={dashboardStyles.productWholesale}>
                    {product.isWholesale ? 'Wholesale' : 'Retail'}
                  </span>
                  <span style={{
                    ...dashboardStyles.productStatus,
                    color: product.isActive ? '#4CAF50' : '#ff4444'
                  }}>
                    {product.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div style={dashboardStyles.productActions}>
                <button 
                  style={dashboardStyles.editButton}
                  onClick={() => handleEditProduct(product)}
                >
                  Edit
                </button>
                <button 
                  style={dashboardStyles.toggleButton}
                  onClick={() => handleDeactivate(product.id, product.isActive ? 'Deactivate' : 'Activate' )}
                >
                  {product.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button 
                  style={dashboardStyles.deleteButton}
                  onClick={() => handleDelete(product.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Product Form */}
      {(isAddingProduct || editingProduct) && (
        <div style={dashboardStyles.formOverlay}>
          <div style={dashboardStyles.formCard}>
            <h3 style={dashboardStyles.formTitle}>
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </h3>
            <form onSubmit={editingProduct ? handleUpdateProduct : handleAddProduct}>
              <div style={dashboardStyles.formGrid}>
                <div style={dashboardStyles.formGroup}>
                  <label style={dashboardStyles.label}>Product Name</label>
                  <input
                    type="text"
                    name="name"
                    value={productForm.name}
                    onChange={handleInputChange}
                    required
                    style={dashboardStyles.input}
                    placeholder="Enter product name"
                  />
                </div>
                <div style={dashboardStyles.formGroup}>
                  <label style={dashboardStyles.label}>Price ($ per kg)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="price"
                    value={productForm.price}
                    onChange={handleInputChange}
                    required
                    style={dashboardStyles.input}
                    placeholder="0.00"
                  />
                </div>
                <div style={dashboardStyles.formGroup}>
                  <label style={dashboardStyles.label}>Weight/Unit</label>
                  <input
                    type="text"
                    name="weight"
                    value={productForm.weight}
                    onChange={handleInputChange}
                    required
                    style={dashboardStyles.input}
                    placeholder="e.g., 1kg, 10kg+"
                  />
                </div>
                <div style={dashboardStyles.formGroup}>
                  <label style={dashboardStyles.label}>Category</label>
                  <select
                    name="category"
                    value={productForm.category}
                    onChange={handleInputChange}
                    style={dashboardStyles.input}
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div style={dashboardStyles.formGroup}>
                <label style={dashboardStyles.label}>Description</label>
                <textarea
                  name="description"
                  value={productForm.description}
                  onChange={handleInputChange}
                  required
                  rows="3"
                  style={dashboardStyles.textarea}
                  placeholder="Enter product description"
                />
              </div>

              {/* Image Input Section */}
              <div style={dashboardStyles.formGroup}>
                <label style={dashboardStyles.label}>Product Image</label>
                
                {/* Image Input Type Selection */}
                <div style={dashboardStyles.radioGroup}>
                  <label style={dashboardStyles.radioLabel}>
                    <input
                      type="radio"
                      value="url"
                      checked={imageInputType === 'url'}
                      onChange={(e) => {
                        setImageInputType(e.target.value);
                        resetImageInput();
                      }}
                      style={dashboardStyles.radioInput}
                    />
                    Use Image URL
                  </label>
                  <label style={dashboardStyles.radioLabel}>
                    <input
                      type="radio"
                      value="upload"
                      checked={imageInputType === 'upload'}
                      onChange={(e) => {
                        setImageInputType(e.target.value);
                        setProductForm(prev => ({ ...prev, image: '' }));
                      }}
                      style={dashboardStyles.radioInput}
                    />
                    Upload Image
                  </label>
                </div>

                {/* Conditional Rendering based on input type */}
                {imageInputType === 'url' ? (
                  <input
                    type="url"
                    name="image"
                    value={productForm.image}
                    onChange={handleInputChange}
                    required
                    style={dashboardStyles.input}
                    placeholder="https://example.com/image.jpg"
                  />
                ) : (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageFileChange}
                      style={dashboardStyles.fileInput}
                    />
                    {isUploading && (
                      <div style={dashboardStyles.uploadProgress}>
                        <div 
                          style={{
                            ...dashboardStyles.progressBar,
                            width: `${uploadProgress}%`
                          }}
                        >
                          {uploadProgress}%
                        </div>
                      </div>
                    )}
                    {imageFile && (
                      <div style={dashboardStyles.imagePreview}>
                        <img 
                          src={productForm.image} 
                          alt="Preview" 
                          style={dashboardStyles.previewImage}
                        />
                        <button 
                          type="button"
                          onClick={resetImageInput}
                          style={dashboardStyles.removeImageButton}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={dashboardStyles.checkboxGroup}>
                <label style={dashboardStyles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="isWholesale"
                    checked={productForm.isWholesale}
                    onChange={handleInputChange}
                    style={dashboardStyles.checkbox}
                  />
                  Wholesale Product
                </label>
              </div>

              {editingProduct && (
                <div style={dashboardStyles.checkboxGroup}>
                  <label style={dashboardStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={productForm.isActive}
                      onChange={handleInputChange}
                      style={dashboardStyles.checkbox}
                    />
                    Active Product
                  </label>
                </div>
              )}

              <div style={dashboardStyles.formActions}>
                <button 
                  type="submit" 
                  style={{
                    ...dashboardStyles.submitButton,
                    opacity: isUploading ? 0.7 : 1,
                    cursor: isUploading ? 'not-allowed' : 'pointer'
                  }}
                  disabled={isUploading}
                >
                  {isUploading ? 'Uploading...' : (editingProduct ? 'Update Product' : 'Add Product')}
                </button>
                <button 
                  type="button" 
                  style={dashboardStyles.cancelButton}
                  onClick={resetForm}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {products.length === 0 && !isAddingProduct && (
        <div style={dashboardStyles.emptyState}>
          <p style={dashboardStyles.emptyText}>No products found. Add your first product to get started!</p>
        </div>
      )}
    </div>
  );
}
// Featured Products Management Component (Staff Only)
function FeaturedProductsManagement({ products, featuredProducts, updateFeaturedProducts, fetchFeaturedProducts, isMobile }) {
  const [availableProducts, setAvailableProducts] = useState([]);

  useEffect(() => {
    const available = products.filter(product => 
      product.isActive && !featuredProducts.some(fp => fp.id === product.id)
    );
    setAvailableProducts(available);
  }, [products, featuredProducts]);

  const addToFeatured = (product) => {
    const newFeaturedProduct = {
      id: product.id,
      name: product.name,
      image: product.image,
      description: product.description.substring(0, 100) + '...'
    };
    
    const updatedFeatured = [...featuredProducts, newFeaturedProduct];
    updateFeaturedProducts(updatedFeatured);
    alert('Product added to featured!');
  };

  const removeFromFeatured = (productId) => {
    const updatedFeatured = featuredProducts.filter(fp => fp.id !== productId);
    updateFeaturedProducts(updatedFeatured);
    alert('Product removed from featured!');
  };

  return (
    <div style={dashboardStyles.section}>
      <h2 style={dashboardStyles.sectionTitle}>Featured Products Management</h2>
      
      <div style={dashboardStyles.subSection}>
        <h3 style={dashboardStyles.subSectionTitle}>Current Featured Products</h3>
        <div style={dashboardStyles.featuredList}>
          {featuredProducts.map((product) => (
            <div key={product.id} style={dashboardStyles.featuredItem}>
              <img 
                src={product.image} 
                alt={product.name} 
                style={dashboardStyles.featuredImage}
              />
              <div style={dashboardStyles.featuredInfo}>
                <h4 style={dashboardStyles.featuredName}>{product.name}</h4>
                <p style={dashboardStyles.featuredDescription}>{product.description}</p>
              </div>
              <div style={dashboardStyles.featuredActions}>
                <button 
                  style={dashboardStyles.removeButton}
                  onClick={() => removeFromFeatured(product.id)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>

        {featuredProducts.length === 0 && (
          <div style={dashboardStyles.emptyState}>
            <p style={dashboardStyles.emptyText}>No featured products yet. Add some products to showcase them on the homepage!</p>
          </div>
        )}
      </div>

      <div style={dashboardStyles.subSection}>
        <h3 style={dashboardStyles.subSectionTitle}>Available Products</h3>
        <div style={dashboardStyles.availableProducts}>
          {availableProducts.map(product => (
            <div key={product.id} style={dashboardStyles.availableItem}>
              <img 
                src={product.image} 
                alt={product.name} 
                style={dashboardStyles.availableImage}
              />
              <div style={dashboardStyles.availableInfo}>
                <h4 style={dashboardStyles.availableName}>{product.name}</h4>
                <p style={dashboardStyles.availableDescription}>
                  {product.description.substring(0, 80)}...
                </p>
                <span style={dashboardStyles.availablePrice}>${product.price}/kg</span>
              </div>
              <button 
                style={dashboardStyles.addFeaturedButton}
                onClick={() => addToFeatured(product)}
              >
                Add to Featured
              </button>
            </div>
          ))}
        </div>

        {availableProducts.length === 0 && (
          <div style={dashboardStyles.emptyState}>
            <p style={dashboardStyles.emptyText}>
              {products.length === 0 
                ? 'No products available. Add products first.' 
                : 'All active products are already featured.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Order Management Component (Staff Only)
function OrderManagement({ isMobile, user }) {
  const [orders, setOrders] = useState([]);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState('');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.post(`${API_URL}/index.php`, {
        apiType: "getOrders",
        page: currentPage,
        limit: itemsPerPage,
        filters: filters
      });
      
      if (response.data.success) {
        setOrders(response.data.orders);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      // Mock data
      setOrders([
        { 
          id: '1234', 
          customer_name: 'John Doe', 
          total: 45.99, 
          status: 'pending', 
          created_at: '2024-01-15',
          payment_status: 'pending',
          item_count: 3
        },
        { 
          id: '1235', 
          customer_name: 'Jane Smith', 
          total: 78.50, 
          status: 'processing', 
          created_at: '2024-01-15',
          payment_status: 'paid',
          item_count: 5
        }
      ]);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      const response = await axios.post(`${API_URL}/index.php`, {
        apiType: "updateOrderStatus",
        orderId: orderId,
        status: newStatus
      });

      if (response.data.success) {
        await fetchOrders();
        console.log("status result", response.data)
        alert('Order status updated successfully!');
        setStatusUpdate('');
      } else {
        alert('Failed to update order status.');
      }
    } catch (error) {
      console.error('Update status error:', error);
      alert('Failed to update order status.');
    }
  };

  const handleExport = (exportType) => {
    try {
      const exportData = orders.map((order, index) => ({
        'SN': index + 1,
        'Order ID': order.id,
        'Customer': order.customer_name,
        'Date': order.created_at,
        'Total': `$${order.total}`,
        'Status': order.status,
        'Payment Status': order.payment_status,
        'Items': order.item_count
      }));

      if (exportType === 'excel') {
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Orders");
        XLSX.writeFile(wb, `orders_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      } else if (exportType === 'pdf') {
        const { jsPDF } = window.jspdf;
        const doc = new jsPDF();
        
        doc.setFontSize(16);
        doc.text('Orders Export', 14, 15);
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 22);
        
        const tableData = exportData.map(item => [
          item.SN,
          item['Order ID'],
          item.Customer,
          item.Date,
          item.Total,
          item.Status,
          item['Payment Status'],
          item.Items
        ]);

        doc.autoTable({
          startY: 25,
          head: [['SN', 'Order ID', 'Customer', 'Date', 'Total', 'Status', 'Payment Status', 'Items']],
          body: tableData,
          theme: 'grid',
          styles: { fontSize: 8 },
          headStyles: { fillColor: [26, 26, 26] }
        });

        doc.save(`orders_export_${new Date().toISOString().split('T')[0]}.pdf`);
      }
      
      alert(`Orders exported successfully as ${exportType.toUpperCase()}!`);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export orders.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return '#4CAF50';
      case 'processing': return '#2196f3';
      case 'pending': return '#ff9800';
      case 'cancelled': return '#f44336';
      default: return '#666';
    }
  };

  const statusOptions = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];

  const filteredOrders = orders.filter(order => {
    return Object.keys(filters).every(key => {
      if (!filters[key]) return true;
      return String(order[key]).toLowerCase().includes(filters[key].toLowerCase());
    });
  });

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div style={dashboardStyles.section}>
      <div style={dashboardStyles.sectionHeader}>
        <h2 style={dashboardStyles.sectionTitle}>Order Management</h2>
        <div style={dashboardStyles.exportButtons}>
          <button 
            style={dashboardStyles.exportButton}
            onClick={() => handleExport('excel')}
          >
            📊 Export Excel
          </button>
          <button 
            style={dashboardStyles.exportButton}
            onClick={() => handleExport('pdf')}
          >
            📄 Export PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={dashboardStyles.filtersContainer}>
        <div style={dashboardStyles.filterRow}>
          <input
            type="text"
            placeholder="Filter by order ID..."
            value={filters.id || ''}
            onChange={(e) => handleFilterChange('id', e.target.value)}
            style={dashboardStyles.filterInput}
          />
          <input
            type="text"
            placeholder="Filter by customer..."
            value={filters.customer_name || ''}
            onChange={(e) => handleFilterChange('customer_name', e.target.value)}
            style={dashboardStyles.filterInput}
          />
          <input
            type="text"
            placeholder="Filter by status..."
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            style={dashboardStyles.filterInput}
          />
        </div>
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div style={dashboardStyles.formOverlay}>
          <div style={dashboardStyles.formCard}>
            <h3 style={dashboardStyles.formTitle}>Order Details - #{selectedOrder.id}</h3>
            <div style={dashboardStyles.orderDetails}>
              <div style={dashboardStyles.detailRow}>
                <strong>Order ID:</strong> {selectedOrder.id}
              </div>
              <div style={dashboardStyles.detailRow}>
                <strong>Customer:</strong> {selectedOrder.customer_name}
              </div>
              <div style={dashboardStyles.detailRow}>
                <strong>Date:</strong> {selectedOrder.created_at}
              </div>
              <div style={dashboardStyles.detailRow}>
                <strong>Status:</strong> 
                <span style={{
                  ...dashboardStyles.statusBadge,
                  backgroundColor: getStatusColor(selectedOrder.status)
                }}>
                  {selectedOrder.status}
                </span>
              </div>
              <div style={dashboardStyles.detailRow}>
                <strong>Payment Status:</strong> {selectedOrder.payment_status}
              </div>
              <div style={dashboardStyles.detailRow}>
                <strong>Total:</strong> ${selectedOrder.total}
              </div>
              <div style={dashboardStyles.detailRow}>
                <strong>Items:</strong> {selectedOrder.item_count}
              </div>
            </div>
            <div style={dashboardStyles.formActions}>
              <button 
                style={dashboardStyles.cancelButton}
                onClick={() => setSelectedOrder(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {statusUpdate && (
        <div style={dashboardStyles.formOverlay}>
          <div style={dashboardStyles.formCard}>
            <h3 style={dashboardStyles.formTitle}>Update Order Status</h3>
            <div style={dashboardStyles.formGroup}>
              <label style={dashboardStyles.label}>New Status</label>
              <select
                value={statusUpdate.newStatus}
                onChange={(e) => setStatusUpdate(prev => ({ ...prev, newStatus: e.target.value }))}
                style={dashboardStyles.input}
              >
                {statusOptions.map(status => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div style={dashboardStyles.formActions}>
              <button 
                style={dashboardStyles.submitButton}
                onClick={() => handleUpdateStatus(statusUpdate.orderId, statusUpdate.newStatus)}
              >
                Update Status
              </button>
              <button 
                style={dashboardStyles.cancelButton}
                onClick={() => setStatusUpdate('')}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div style={dashboardStyles.tableContainer}>
        <table style={dashboardStyles.table}>
          <thead>
            <tr>
              <th style={dashboardStyles.tableHeader}>SN</th>
              <th style={dashboardStyles.tableHeader}>Order ID</th>
              <th style={dashboardStyles.tableHeader}>Customer</th>
              <th style={dashboardStyles.tableHeader}>Date</th>
              <th style={dashboardStyles.tableHeader}>Total</th>
              <th style={dashboardStyles.tableHeader}>Status</th>
              <th style={dashboardStyles.tableHeader}>Payment</th>
              <th style={dashboardStyles.tableHeader}>Items</th>
              <th style={dashboardStyles.tableHeader}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedOrders.map((order, index) => (
              <tr key={order.id} style={dashboardStyles.tableRow}>
                <td style={dashboardStyles.tableCell}>
                  {(currentPage - 1) * itemsPerPage + index + 1}
                </td>
                <td style={dashboardStyles.tableCell}>#{order.id}</td>
                <td style={dashboardStyles.tableCell}>{order.customer_name}</td>
                <td style={dashboardStyles.tableCell}>{order.created_at}</td>
                <td style={dashboardStyles.tableCell}>${order.total}</td>
                <td style={dashboardStyles.tableCell}>
                  <span style={{
                    ...dashboardStyles.statusBadge,
                    backgroundColor: getStatusColor(order.status)
                  }}>
                    {order.status}
                  </span>
                </td>
                <td style={dashboardStyles.tableCell}>
                  <span style={{
                    ...dashboardStyles.statusBadge,
                    backgroundColor: order.payment_status === 'paid' ? '#4CAF50' : '#ff9800'
                  }}>
                    {order.payment_status}
                  </span>
                </td>
                <td style={dashboardStyles.tableCell}>{order.item_count}</td>
                <td style={dashboardStyles.tableCell}>
                  <div style={dashboardStyles.actionButtons}>
                    <button 
                      style={dashboardStyles.viewButton}
                      onClick={() => handleViewDetails(order)}
                    >
                      View Details
                    </button>
                    <button 
                      style={dashboardStyles.updateButton}
                      onClick={() => setStatusUpdate({ orderId: order.id, newStatus: order.status })}
                    >
                      Update Status
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div style={dashboardStyles.pagination}>
          <div style={dashboardStyles.paginationInfo}>
            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredOrders.length)} of {filteredOrders.length} orders
          </div>
          <div style={dashboardStyles.paginationControls}>
            <button 
              style={dashboardStyles.paginationButton}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
            >
              Previous
            </button>
            <span style={dashboardStyles.paginationNumbers}>
              Page {currentPage} of {totalPages}
            </span>
            <button 
              style={dashboardStyles.paginationButton}
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
            >
              Next
            </button>
          </div>
          <div style={dashboardStyles.pageSizeSelector}>
            <label>Rows per page: </label>
            <select 
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={dashboardStyles.pageSizeSelect}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

function UserManagement({ isMobile }) {
  const [users, setUsers] = useState([]);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'customer',
    isVerified: false
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    
    try {
      const response = await axios.post(`${API_URL}/index.php`, {
        apiType: "getUsers",
        page: currentPage,
        limit: itemsPerPage,
        filters: filters
      });
      
      if (response.data.success) {
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/index.php`, {
        apiType: "createUser",
        ...userForm
      });

      console.log("User Creation Response - Data", response.data);

      if (response.data.success) {
        await fetchUsers();
        setIsAddingUser(false);
        
        setUserForm({
          name: '',
          email: '',
          phone: '',
          role: 'customer',
          isVerified: false
         // password: ''
        });
        //alert('User created successfully!');
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Create user error:', error);
      alert('Failed to create user.');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const response = await axios.post(`${API_URL}/index.php`, {
          apiType: "deleteUser",
          userId: userId
        });

        if (response.data.success) {
          await fetchUsers();
          alert('User deleted successfully!');
        }
      } catch (error) {
        console.error('Delete user error:', error);
        alert('Failed to delete user.');
      }
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/index.php`, {
        apiType: "updateUser",
        id: editingUser.id,
        ...userForm
      });

      console.log("User Update Response - Data", response.data);

      if (response.data.success) {
        await fetchUsers();
        console.log("User Update Result = ", response.data)
        setEditingUser(null);
        setUserForm({
          name: '',
          email: '',
          phone: '',
          role: 'customer',
          isVerified:false
        });
        alert(response.data.message || 'User updated successfully!');
      } else {
        alert(response.data.message || 'Failed to update user.');
      }
    } catch (error) {
      console.error('Update user error:', error);
      alert('Failed to update user.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Add this useEffect to populate the form when editing a user
  useEffect(() => {
    if (editingUser) {
      setUserForm({
        name: editingUser.name || '',
        email: editingUser.email || '',
        phone: editingUser.phone || '',
        role: editingUser.role || 'customer',
        isVerified:editingUser.isVerified || false
      });
    }
  }, [editingUser]);



  return (
    <div style={dashboardStyles.section}>
      <div style={dashboardStyles.sectionHeader}>
        <h2 style={dashboardStyles.sectionTitle}>User Management</h2>
        <button 
          style={dashboardStyles.addButton}
          onClick={() => setIsAddingUser(true)}
        >
          + Create User
        </button>
      </div>

     
      {isAddingUser && (
        <div style={dashboardStyles.formOverlay}>
          <div style={dashboardStyles.formCard}>
            <h3 style={dashboardStyles.formTitle}>Create New User</h3>
            <form onSubmit={handleCreateUser}>
              <div style={dashboardStyles.formGrid}>
                <div style={dashboardStyles.formGroup}>
                  <label style={dashboardStyles.label}>Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={userForm.name}
                    onChange={handleInputChange}
                    required
                    style={dashboardStyles.input}
                  />
                </div>
                <div style={dashboardStyles.formGroup}>
                  <label style={dashboardStyles.label}>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={userForm.email}
                    onChange={handleInputChange}
                    required
                    style={dashboardStyles.input}
                  />
                </div>
                <div style={dashboardStyles.formGroup}>
                  <label style={dashboardStyles.label}>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={userForm.phone}
                    onChange={handleInputChange}
                    style={dashboardStyles.input}
                  />
                </div>
                <div style={dashboardStyles.formGroup}>
                  <label style={dashboardStyles.label}>Role</label>
                  <select
                    name="role"
                    value={userForm.role}
                    onChange={handleInputChange}
                    style={dashboardStyles.input}
                  >
                    <option value="customer">Customer</option>
                    <option value="staff">Staff</option>
                  </select>
                </div>
              </div>
              <div style={dashboardStyles.formActions}>
                <button type="submit" style={dashboardStyles.submitButton}>
                  Create User
                </button>
                <button 
                  type="button" 
                  style={dashboardStyles.cancelButton}
                  onClick={() => setIsAddingUser(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User edit form */}
     {editingUser && (
  <div style={dashboardStyles.formOverlay}>
    <div style={dashboardStyles.formCard}>
      <h3 style={dashboardStyles.formTitle}>Edit User</h3>
      <form onSubmit={handleEditUser}>
        <div style={dashboardStyles.formGrid}>
          <div style={dashboardStyles.formGroup}>
            <label style={dashboardStyles.label}>Full Name</label>
            <input
              type="text"
              name="name"
              value={userForm.name}
              onChange={handleInputChange}
              required
              style={dashboardStyles.input}
            />
          </div>
          <div style={dashboardStyles.formGroup}>
            <label style={dashboardStyles.label}>Email</label>
            <input
              type="email"
              name="email"
              value={userForm.email}
              onChange={handleInputChange}
              required
              style={dashboardStyles.input}
            />
          </div>
          <div style={dashboardStyles.formGroup}>
            <label style={dashboardStyles.label}>Phone</label>
            <input
              type="tel"
              name="phone"
              value={userForm.phone}
              onChange={handleInputChange}
              style={dashboardStyles.input}
            />
          </div>
          <div style={dashboardStyles.formGroup}>
            <label style={dashboardStyles.label}>Role</label>
            <select
              name="role"
              value={userForm.role}
              onChange={handleInputChange}
              style={dashboardStyles.input}
            >
              <option value="customer">Customer</option>
              <option value="staff">Staff</option>
            </select>
          </div>
          <div style={dashboardStyles.formGroup}>
            <label style={dashboardStyles.label}>Verified Status</label>
            <select
              name="isVerified"
              value={userForm.isVerified}
              onChange={handleInputChange}
              style={dashboardStyles.input}
            >
              <option value="true">True</option>
              <option value="false">False</option>
            </select>
          </div>
        </div>
        <div style={dashboardStyles.formActions}>
          <button type="submit" style={dashboardStyles.submitButton}>
            Update User
          </button>
          <button 
            type="button" 
            style={dashboardStyles.cancelButton}
            onClick={() => setEditingUser(null)}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  </div>
)}

      
      <div style={dashboardStyles.tableContainer}>
        <table style={dashboardStyles.table}>
          <thead>
            <tr>
              <th style={dashboardStyles.tableHeader}>SN</th>
              <th style={dashboardStyles.tableHeader}>Name</th>
              <th style={dashboardStyles.tableHeader}>Email</th>
              <th style={dashboardStyles.tableHeader}>Phone</th>
              <th style={dashboardStyles.tableHeader}>Role</th>
              <th style={dashboardStyles.tableHeader}>Status</th>
              <th style={dashboardStyles.tableHeader}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user, index) => (
              <tr key={user.id} style={dashboardStyles.tableRow}>
                <td style={dashboardStyles.tableCell}>{index + 1}</td>
                <td style={dashboardStyles.tableCell}>{user.name}</td>
                <td style={dashboardStyles.tableCell}>{user.email}</td>
                <td style={dashboardStyles.tableCell}>{user.phone}</td>
                <td style={dashboardStyles.tableCell}>
                  <span style={{
                    ...dashboardStyles.roleBadge,
                    backgroundColor: user.role === 'staff' ? '#d4af37' : '#2196f3'
                  }}>
                    {user.role}
                  </span>
                </td>
                <td style={dashboardStyles.tableCell}>
                  <span style={{
                    ...dashboardStyles.statusBadge,
                    backgroundColor: user.isVerified ? '#4CAF50' : '#ff9800'
                  }}>
                    {user.isVerified ? 'Verified' : 'Pending'}
                  </span>
                </td>
                <td style={dashboardStyles.tableCell}>
                  <div style={dashboardStyles.actionButtons}>
                    <button 
                      style={dashboardStyles.editButton}
                      onClick={() => setEditingUser(user)}
                    >
                      Edit
                    </button>
                    <button 
                      style={dashboardStyles.deleteButton}
                      onClick={() => handleDeleteUser(user.id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Sales Report Component (Staff Only)
function SalesReport({ isMobile }) {
  const [reportData, setReportData] = useState({
    period: 'today',
    startDate: '',
    endDate: ''
  });
  const [salesData, setSalesData] = useState(null);
  const [individualSales, setIndividualSales] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReportData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const generateReport = async () => {
    setIsLoading(true);
    try {
      // Fetch sales data from backend
      const response = await axios.post(`${API_URL}/index.php`, {
        apiType: "getSalesReport",
        period: reportData.period,
        startDate: reportData.startDate,
        endDate: reportData.endDate
      });
console.log("Sales Report Generated Result = ", response.data);
      if (response.data.success) {
        setSalesData(response.data.summary);
        setIndividualSales(response.data.individualSales || []);
      } else {
        // Fallback to mock data if API fails
        const mockData = {
          totalSales: 1240.50,
          totalOrders: 18,
          averageOrder: 68.92,
          topProducts: [
            { name: 'Pork Legs', sales: 450.25, quantity: 28 },
            { name: 'Pork Shoulders', sales: 320.75, quantity: 25 },
            { name: 'Pork Belly', sales: 280.50, quantity: 18 }
          ],
          salesByCategory: [
            { category: 'Premium Cuts', sales: 680.25 },
            { category: 'BBQ Specials', sales: 320.75 },
            { category: 'Traditional Cuts', sales: 239.50 }
          ]
        };
        setSalesData(mockData);
        
        // Mock individual sales data
        const mockIndividualSales = [
          { id: 1, orderId: 'ORD001', customerName: 'John Doe', product: 'Pork Legs', quantity: 2, price: 15.99, total: 31.98, date: '2024-01-15', status: 'completed' },
          { id: 2, orderId: 'ORD002', customerName: 'Jane Smith', product: 'Pork Shoulders', quantity: 1, price: 12.99, total: 12.99, date: '2024-01-15', status: 'completed' },
          { id: 3, orderId: 'ORD003', customerName: 'Mike Johnson', product: 'Pork Belly', quantity: 3, price: 9.99, total: 29.97, date: '2024-01-14', status: 'completed' },
          { id: 4, orderId: 'ORD004', customerName: 'Sarah Wilson', product: 'Pork Legs', quantity: 1, price: 15.99, total: 15.99, date: '2024-01-14', status: 'completed' },
          { id: 5, orderId: 'ORD005', customerName: 'David Brown', product: 'Pork Shoulders', quantity: 2, price: 12.99, total: 25.98, date: '2024-01-13', status: 'completed' }
        ];
        setIndividualSales(mockIndividualSales);
      }
    } catch (error) {
      console.error('Error fetching sales report:', error);
      // Use mock data as fallback
      const mockData = {
        totalSales: 1240.50,
        totalOrders: 18,
        averageOrder: 68.92,
        topProducts: [
          { name: 'Pork Legs', sales: 450.25, quantity: 28 },
          { name: 'Pork Shoulders', sales: 320.75, quantity: 25 },
          { name: 'Pork Belly', sales: 280.50, quantity: 18 }
        ],
        salesByCategory: [
          { category: 'Premium Cuts', sales: 680.25 },
          { category: 'BBQ Specials', sales: 320.75 },
          { category: 'Traditional Cuts', sales: 239.50 }
        ]
      };
      setSalesData(mockData);
      
      const mockIndividualSales = [
        { id: 1, orderId: 'ORD001', customerName: 'John Doe', product: 'Pork Legs', quantity: 2, price: 15.99, total: 31.98, date: '2024-01-15', status: 'completed' },
        { id: 2, orderId: 'ORD002', customerName: 'Jane Smith', product: 'Pork Shoulders', quantity: 1, price: 12.99, total: 12.99, date: '2024-01-15', status: 'completed' },
        { id: 3, orderId: 'ORD003', customerName: 'Mike Johnson', product: 'Pork Belly', quantity: 3, price: 9.99, total: 29.97, date: '2024-01-14', status: 'completed' }
      ];
      setIndividualSales(mockIndividualSales);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = (exportType) => {
    try {
      const exportData = individualSales.map((sale, index) => ({
        'SN': index + 1,
        'Order ID': sale.orderId,
        'Customer': sale.customerName,
        'Product': sale.product,
        'Quantity': sale.quantity,
        'Price': `$${sale.price}`,
        'Total': `$${sale.total}`,
        'Date': sale.date,
        'Status': sale.status
      }));

      if (exportType === 'excel') {
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sales");
        XLSX.writeFile(wb, `sales_report_${new Date().toISOString().split('T')[0]}.xlsx`);
      } else if (exportType === 'pdf') {
        const { jsPDF } = window.jspdf;
        const doc = new jsPDF();
        
        doc.setFontSize(16);
        doc.text('Sales Report - Individual Sales', 14, 15);
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 22);
        doc.text(`Period: ${reportData.period}`, 14, 28);
        
        const tableData = exportData.map(item => [
          item.SN,
          item['Order ID'],
          item.Customer,
          item.Product,
          item.Quantity,
          item.Price,
          item.Total,
          item.Date,
          item.Status
        ]);

        doc.autoTable({
          startY: 35,
          head: [['SN', 'Order ID', 'Customer', 'Product', 'Quantity', 'Price', 'Total', 'Date', 'Status']],
          body: tableData,
          theme: 'grid',
          styles: { fontSize: 8 },
          headStyles: { fillColor: [26, 26, 26] }
        });

        doc.save(`sales_report_${new Date().toISOString().split('T')[0]}.pdf`);
      }
      
      alert(`Sales report exported successfully as ${exportType.toUpperCase()}!`);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export sales report.');
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(individualSales.length / itemsPerPage);
  const paginatedSales = individualSales.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#4CAF50';
      case 'processing': return '#2196f3';
      case 'pending': return '#ff9800';
      case 'cancelled': return '#f44336';
      default: return '#666';
    }
  };

  return (
    <div style={dashboardStyles.section}>
      <h2 style={dashboardStyles.sectionTitle}>Sales Report</h2>
      
      <div style={dashboardStyles.formCard}>
        <div style={dashboardStyles.formGrid}>
          <div style={dashboardStyles.formGroup}>
            <label style={dashboardStyles.label}>Report Period</label>
            <select
              name="period"
              value={reportData.period}
              onChange={handleInputChange}
              style={dashboardStyles.input}
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {reportData.period === 'custom' && (
            <>
              <div style={dashboardStyles.formGroup}>
                <label style={dashboardStyles.label}>Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={reportData.startDate}
                  onChange={handleInputChange}
                  style={dashboardStyles.input}
                />
              </div>
              <div style={dashboardStyles.formGroup}>
                <label style={dashboardStyles.label}>End Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={reportData.endDate}
                  onChange={handleInputChange}
                  style={dashboardStyles.input}
                />
              </div>
            </>
          )}
        </div>

        <button 
          style={{
            ...dashboardStyles.submitButton,
            opacity: isLoading ? 0.7 : 1,
            cursor: isLoading ? 'not-allowed' : 'pointer'
          }}
          onClick={generateReport}
          disabled={isLoading}
        >
          {isLoading ? 'Generating Report...' : 'Generate Report'}
        </button>
      </div>

      {salesData && (
        <div style={dashboardStyles.salesReport}>
          <div style={dashboardStyles.salesSummary}>
            <h3 style={dashboardStyles.sectionTitle}>Sales Summary</h3>
            <div style={dashboardStyles.statsGrid}>
              <div style={dashboardStyles.statCard}>
                <h3 style={dashboardStyles.statValue}>${salesData.totalSales.toFixed(2)}</h3>
                <p style={dashboardStyles.statLabel}>Total Sales</p>
              </div>
              <div style={dashboardStyles.statCard}>
                <h3 style={dashboardStyles.statValue}>{salesData.totalOrders}</h3>
                <p style={dashboardStyles.statLabel}>Total Orders</p>
              </div>
              <div style={dashboardStyles.statCard}>
                <h3 style={dashboardStyles.statValue}>${salesData.averageOrder.toFixed(2)}</h3>
                <p style={dashboardStyles.statLabel}>Average Order</p>
              </div>
            </div>
          </div>

          <div style={dashboardStyles.salesDetails}>
            <div style={dashboardStyles.detailSection}>
              <h4 style={dashboardStyles.subSectionTitle}>Top Products</h4>
              <div style={dashboardStyles.tableContainer}>
                <table style={dashboardStyles.table}>
                  <thead>
                    <tr>
                      <th style={dashboardStyles.tableHeader}>Product</th>
                      <th style={dashboardStyles.tableHeader}>Sales</th>
                      <th style={dashboardStyles.tableHeader}>Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesData.topProducts.map((product, index) => (
                      <tr key={index} style={dashboardStyles.tableRow}>
                        <td style={dashboardStyles.tableCell}>{product.name}</td>
                        <td style={dashboardStyles.tableCell}>${product.sales.toFixed(2)}</td>
                        <td style={dashboardStyles.tableCell}>{product.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={dashboardStyles.detailSection}>
              <h4 style={dashboardStyles.subSectionTitle}>Sales by Category</h4>
              <div style={dashboardStyles.tableContainer}>
                <table style={dashboardStyles.table}>
                  <thead>
                    <tr>
                      <th style={dashboardStyles.tableHeader}>Category</th>
                      <th style={dashboardStyles.tableHeader}>Sales</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesData.salesByCategory.map((category, index) => (
                      <tr key={index} style={dashboardStyles.tableRow}>
                        <td style={dashboardStyles.tableCell}>{category.category}</td>
                        <td style={dashboardStyles.tableCell}>${category.sales.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Individual Sales Table */}
          <div style={dashboardStyles.detailSection}>
            <div style={dashboardStyles.sectionHeader}>
              <h4 style={dashboardStyles.subSectionTitle}>Individual Sales</h4>
              <div style={dashboardStyles.exportButtons}>
                <button 
                  style={dashboardStyles.exportButton}
                  onClick={() => handleExport('excel')}
                >
                  📊 Export Excel
                </button>
                <button 
                  style={dashboardStyles.exportButton}
                  onClick={() => handleExport('pdf')}
                >
                  📄 Export PDF
                </button>
              </div>
            </div>
            
            <div style={dashboardStyles.tableContainer}>
              <table style={dashboardStyles.table}>
                <thead>
                  <tr>
                    <th style={dashboardStyles.tableHeader}>SN</th>
                    <th style={dashboardStyles.tableHeader}>Order ID</th>
                    <th style={dashboardStyles.tableHeader}>Customer</th>
                    <th style={dashboardStyles.tableHeader}>Product</th>
                    <th style={dashboardStyles.tableHeader}>Quantity</th>
                    <th style={dashboardStyles.tableHeader}>Price</th>
                    <th style={dashboardStyles.tableHeader}>Total</th>
                    <th style={dashboardStyles.tableHeader}>Date</th>
                    <th style={dashboardStyles.tableHeader}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSales.map((sale, index) => (
                    <tr key={sale.id} style={dashboardStyles.tableRow}>
                      <td style={dashboardStyles.tableCell}>
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td style={dashboardStyles.tableCell}>{sale.orderId}</td>
                      <td style={dashboardStyles.tableCell}>{sale.customerName}</td>
                      <td style={dashboardStyles.tableCell}>{sale.product}</td>
                      <td style={dashboardStyles.tableCell}>{sale.quantity}</td>
                      <td style={dashboardStyles.tableCell}>${sale.price}</td>
                      <td style={dashboardStyles.tableCell}>${sale.total}</td>
                      <td style={dashboardStyles.tableCell}>{sale.date}</td>
                      <td style={dashboardStyles.tableCell}>
                        <span style={{
                          ...dashboardStyles.statusBadge,
                          backgroundColor: getStatusColor(sale.status)
                        }}>
                          {sale.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {individualSales.length > 0 && (
                <div style={dashboardStyles.pagination}>
                  <div style={dashboardStyles.paginationInfo}>
                    Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, individualSales.length)} of {individualSales.length} sales
                  </div>
                  <div style={dashboardStyles.paginationControls}>
                    <button 
                      style={dashboardStyles.paginationButton}
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(prev => prev - 1)}
                    >
                      Previous
                    </button>
                    <span style={dashboardStyles.paginationNumbers}>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button 
                      style={dashboardStyles.paginationButton}
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(prev => prev + 1)}
                    >
                      Next
                    </button>
                  </div>
                  <div style={dashboardStyles.pageSizeSelector}>
                    <label>Rows per page: </label>
                    <select 
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      style={dashboardStyles.pageSizeSelect}
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {individualSales.length === 0 && (
              <div style={dashboardStyles.emptyState}>
                <p style={dashboardStyles.emptyText}>No individual sales found for the selected period.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Customer Orders Component
function CustomerOrders({ isMobile, user, setCurrentPage }) {
  const [orders, setOrders] = useState([]);
  const [viewMode, setViewMode] = useState('tabular');
  const [filters, setFilters] = useState({});
  const [currentPageNum, setCurrentPageNum] = useState(1); // Renamed from setCurrentPage
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedOrder, setSelectedOrder] = useState(null);
  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.post(`${API_URL}/index.php`, {
        apiType: "getOrders",
        userId: user?.id,
        page: currentPageNum,
        limit: itemsPerPage,
        filters: filters
      });
      
      if (response.data.success) {
        setOrders(response.data.orders);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      // Mock data for demonstration
      setOrders([
        { 
          id: '1234', 
          customer_name: user?.name, 
          total: 45.99, 
          status: 'delivered', 
          created_at: '2024-01-15',
          payment_status: 'paid',
          item_count: 3
        },
        { 
          id: '1235', 
          customer_name: user?.name, 
          total: 78.50, 
          status: 'processing', 
          created_at: '2024-01-14',
          payment_status: 'paid',
          item_count: 5
        }
      ]);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
  };

  const handleReorder = async (order) => {
    try {
      const response = await axios.post(`${API_URL}/index.php`, {
        apiType: "reorder",
        orderId: order.id
      });

      if (response.data.success) {
        alert('Order recreated successfully! You can review it in your cart.');
        // Optionally navigate to cart
      } else {
        alert('Failed to reorder. Please try again.');
      }
    } catch (error) {
      console.error('Reorder error:', error);
      alert('Failed to reorder. Please try again.');
    }
  };

  const handleExport = (exportType) => {
    try {
      const exportData = orders.map((order, index) => ({
        'SN': index + 1,
        'Order ID': order.id,
        'Date': order.created_at,
        'Customer': order.customer_name,
        'Total': `$${order.total}`,
        'Status': order.status,
        'Payment Status': order.payment_status,
        'Items': order.item_count
      }));

      if (exportType === 'excel') {
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Orders");
        XLSX.writeFile(wb, `my_orders_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      } else if (exportType === 'pdf') {
        const { jsPDF } = window.jspdf;
        const doc = new jsPDF();
        
        doc.setFontSize(16);
        doc.text('My Orders Export', 14, 15);
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 22);
        
        const tableData = exportData.map(item => [
          item.SN,
          item['Order ID'],
          item.Date,
          item.Total,
          item.Status,
          item['Payment Status'],
          item.Items
        ]);

        doc.autoTable({
          startY: 25,
          head: [['SN', 'Order ID', 'Date', 'Total', 'Status', 'Payment Status', 'Items']],
          body: tableData,
          theme: 'grid',
          styles: { fontSize: 8 },
          headStyles: { fillColor: [26, 26, 26] }
        });

        doc.save(`my_orders_export_${new Date().toISOString().split('T')[0]}.pdf`);
      }
      
      alert(`Orders exported successfully as ${exportType.toUpperCase()}!`);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export orders.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return '#4CAF50';
      case 'processing': return '#2196f3';
      case 'pending': return '#ff9800';
      case 'cancelled': return '#f44336';
      default: return '#666';
    }
  };

  const filteredOrders = orders.filter(order => {
    return Object.keys(filters).every(key => {
      if (!filters[key]) return true;
      return String(order[key]).toLowerCase().includes(filters[key].toLowerCase());
    });
  });

  // Update all references from currentPage to currentPageNum
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const paginatedOrders = filteredOrders.slice(
    (currentPageNum - 1) * itemsPerPage,
    currentPageNum * itemsPerPage
  );

  return (
    <div style={dashboardStyles.section}>
      <div style={dashboardStyles.sectionHeader}>
        <h2 style={dashboardStyles.sectionTitle}>My Orders</h2>
        <div style={dashboardStyles.headerActions}>
          <select 
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            style={dashboardStyles.viewModeSelect}
          >
            <option value="tabular">Tabular View</option>
            <option value="linear">Linear View</option>
          </select>
        </div>
      </div>

      {/* Filters */}
      <div style={dashboardStyles.filtersContainer}>
        <div style={dashboardStyles.filterRow}>
          <input
            type="text"
            placeholder="Filter by order ID..."
            value={filters.id || ''}
            onChange={(e) => handleFilterChange('id', e.target.value)}
            style={dashboardStyles.filterInput}
          />
          <input
            type="text"
            placeholder="Filter by status..."
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            style={dashboardStyles.filterInput}
          />
          <input
            type="text"
            placeholder="Filter by date..."
            value={filters.created_at || ''}
            onChange={(e) => handleFilterChange('created_at', e.target.value)}
            style={dashboardStyles.filterInput}
          />
        </div>
        
        {/* Export Buttons */}
        <div style={dashboardStyles.exportButtons}>
          <button 
            style={dashboardStyles.exportButton}
            onClick={() => handleExport('excel')}
          >
            📊 Export Excel
          </button>
          <button 
            style={dashboardStyles.exportButton}
            onClick={() => handleExport('pdf')}
          >
            📄 Export PDF
          </button>
        </div>
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div style={dashboardStyles.formOverlay}>
          <div style={dashboardStyles.formCard}>
            <h3 style={dashboardStyles.formTitle}>Order Details - #{selectedOrder.id}</h3>
            <div style={dashboardStyles.orderDetails}>
              <div style={dashboardStyles.detailRow}>
                <strong>Order ID:</strong> {selectedOrder.id}
              </div>
              <div style={dashboardStyles.detailRow}>
                <strong>Date:</strong> {selectedOrder.created_at}
              </div>
              <div style={dashboardStyles.detailRow}>
                <strong>Status:</strong> 
                <span style={{
                  ...dashboardStyles.statusBadge,
                  backgroundColor: getStatusColor(selectedOrder.status)
                }}>
                  {selectedOrder.status}
                </span>
              </div>
              <div style={dashboardStyles.detailRow}>
                <strong>Payment Status:</strong> {selectedOrder.payment_status}
              </div>
              <div style={dashboardStyles.detailRow}>
                <strong>Total:</strong> ${selectedOrder.total}
              </div>
              <div style={dashboardStyles.detailRow}>
                <strong>Items:</strong> {selectedOrder.item_count}
              </div>
            </div>
            <div style={dashboardStyles.formActions}>
              <button 
                style={dashboardStyles.submitButton}
                onClick={() => handleReorder(selectedOrder)}
              >
                Reorder
              </button>
              <button 
                style={dashboardStyles.cancelButton}
                onClick={() => setSelectedOrder(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Orders Table */}
      {viewMode === 'tabular' ? (
        <div style={dashboardStyles.tableContainer}>
          <table style={dashboardStyles.table}>
            <thead>
              <tr>
                <th style={dashboardStyles.tableHeader}>SN</th>
                <th style={dashboardStyles.tableHeader}>Order ID</th>
                <th style={dashboardStyles.tableHeader}>Date</th>
                <th style={dashboardStyles.tableHeader}>Total</th>
                <th style={dashboardStyles.tableHeader}>Status</th>
                <th style={dashboardStyles.tableHeader}>Payment</th>
                <th style={dashboardStyles.tableHeader}>Items</th>
                <th style={dashboardStyles.tableHeader}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.map((order, index) => (
                <tr key={order.id} style={dashboardStyles.tableRow}>
                  <td style={dashboardStyles.tableCell}>
                    {(currentPageNum - 1) * itemsPerPage + index + 1}
                  </td>
                  <td style={dashboardStyles.tableCell}>#{order.id}</td>
                  <td style={dashboardStyles.tableCell}>{order.created_at}</td>
                  <td style={dashboardStyles.tableCell}>${order.total}</td>
                  <td style={dashboardStyles.tableCell}>
                    <span style={{
                      ...dashboardStyles.statusBadge,
                      backgroundColor: getStatusColor(order.status)
                    }}>
                      {order.status}
                    </span>
                  </td>
                  <td style={dashboardStyles.tableCell}>
                    <span style={{
                      ...dashboardStyles.statusBadge,
                      backgroundColor: order.payment_status === 'paid' ? '#4CAF50' : '#ff9800'
                    }}>
                      {order.payment_status}
                    </span>
                  </td>
                  <td style={dashboardStyles.tableCell}>{order.item_count}</td>
                  <td style={dashboardStyles.tableCell}>
                    <div style={dashboardStyles.actionButtons}>
                      <button 
                        style={dashboardStyles.viewButton}
                        onClick={() => handleViewDetails(order)}
                      >
                        View Details
                      </button>
                      <button 
                        style={dashboardStyles.reorderButton}
                        onClick={() => handleReorder(order)}
                      >
                        Reorder
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div style={dashboardStyles.pagination}>
            <div style={dashboardStyles.paginationInfo}>
              Showing {(currentPageNum - 1) * itemsPerPage + 1} to {Math.min(currentPageNum * itemsPerPage, filteredOrders.length)} of {filteredOrders.length} orders
            </div>
            <div style={dashboardStyles.paginationControls}>
             <button 
    style={dashboardStyles.paginationButton}
    disabled={currentPageNum === 1}
    onClick={() => setCurrentPageNum(prev => prev - 1)}
  >
    Previous
  </button>

  <button 
    style={dashboardStyles.paginationButton}
    disabled={currentPageNum === totalPages}
    onClick={() => setCurrentPageNum(prev => prev + 1)}
  >
    Next
  </button>

            </div>
            <div style={dashboardStyles.pageSizeSelector}>
              <label>Rows per page: </label>
                <select 
    value={itemsPerPage}
    onChange={(e) => {
      setItemsPerPage(Number(e.target.value));
      setCurrentPageNum(1);
    }}
    style={dashboardStyles.pageSizeSelect}
  >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
            </div>
          </div>
        </div>
      ) : (
        // Linear View
        <div style={dashboardStyles.ordersList}>
          {paginatedOrders.map((order, index) => (
            <div key={order.id} style={dashboardStyles.orderItem}>
              <div style={dashboardStyles.orderMainInfo}>
                <h4 style={dashboardStyles.orderId}>Order #{order.id}</h4>
                <span style={{
                  ...dashboardStyles.orderStatus,
                  backgroundColor: getStatusColor(order.status)
                }}>
                  {order.status}
                </span>
              </div>
              <div style={dashboardStyles.orderSecondaryInfo}>
                <p style={dashboardStyles.orderTotal}><strong>Total:</strong> ${order.total}</p>
                <p style={dashboardStyles.orderDate}><strong>Date:</strong> {order.created_at}</p>
                <p style={dashboardStyles.orderItems}><strong>Items:</strong> {order.item_count}</p>
                <p style={dashboardStyles.orderPayment}>
                  <strong>Payment:</strong> 
                  <span style={{
                    color: order.payment_status === 'paid' ? '#4CAF50' : '#ff9800',
                    marginLeft: '5px'
                  }}>
                    {order.payment_status}
                  </span>
                </p>
              </div>
              <div style={dashboardStyles.orderActions}>
                <button 
                  style={dashboardStyles.viewButton}
                  onClick={() => handleViewDetails(order)}
                >
                  View Details
                </button>
                <button 
                  style={dashboardStyles.reorderButton}
                  onClick={() => handleReorder(order)}
                >
                  Reorder
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {orders.length === 0 && (
        <div style={dashboardStyles.emptyState}>
          <p style={dashboardStyles.emptyText}>You haven't placed any orders yet.</p>
         <button 
        style={dashboardStyles.ctaButton}
        onClick={() => setCurrentPage('shop')}
      >
        Start Shopping
      </button>
        </div>
      )}
    </div>
  );
}

// Profile Section Component
function ProfileSection({ user, setCurrentPage, isMobile }) {
  return (
    <div style={dashboardStyles.section}>
      <h2 style={dashboardStyles.sectionTitle}>My Profile</h2>
      
      <div style={dashboardStyles.profileCard}>
        <div style={dashboardStyles.profileHeader}>
          {user?.imageUrl ? (
            <img 
              src={user.imageUrl} 
              alt="Profile" 
              style={dashboardStyles.profileImage}
            />
          ) : (
            <div style={dashboardStyles.profileInitials}>
              {user?.name ? user.name.charAt(0).toUpperCase() : user?.email?.charAt(0).toUpperCase()}
            </div>
          )}
          <div style={dashboardStyles.profileInfo}>
            <h3 style={dashboardStyles.profileName}>{user?.name || 'User'}</h3>
            <p style={dashboardStyles.profileEmail}>{user?.email}</p>
            <span style={dashboardStyles.profileRole}>
              {user?.role === 'staff' ? 'Staff Member' : 'Customer'}
            </span>
          </div>
        </div>

        <div style={dashboardStyles.profileDetails}>
          <div style={dashboardStyles.detailItem}>
            <strong>Phone:</strong> {user?.phone || 'Not provided'}
          </div>
          <div style={dashboardStyles.detailItem}>
            <strong>Address:</strong> {user?.address || 'Not provided'}
          </div>
          <div style={dashboardStyles.detailItem}>
            <strong>Member since:</strong> January 2024
          </div>
          <div style={dashboardStyles.detailItem}>
            <strong>Account status:</strong> 
            <span style={{ color: user?.isVerified ? '#4CAF50' : '#ff9800', marginLeft: '8px' }}>
              {user?.isVerified ? 'Verified' : 'Pending Verification'}
            </span>
          </div>
        </div>

        <div style={dashboardStyles.profileActions}>
          <button 
            style={dashboardStyles.primaryButton}
            onClick={() => setCurrentPage('profile')}
          >
            Edit Profile
          </button>
          <button 
            style={dashboardStyles.secondaryButton}
            onClick={() => setCurrentPage('change-password')}
          >
            Change Password
          </button>
        </div>
      </div>
    </div>
  );
}

// Change Password Section Component
function ChangePasswordSection({ setCurrentPage, isMobile }) {
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match!');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      alert('New password must be at least 6 characters long!');
      return;
    }
    alert('Password changed successfully!');
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  return (
    <div style={dashboardStyles.section}>
      <h2 style={dashboardStyles.sectionTitle}>Change Password</h2>
      
      <div style={dashboardStyles.formCard}>
        <form onSubmit={handleSubmit}>
          <div style={dashboardStyles.formGroup}>
            <label style={dashboardStyles.label}>Current Password</label>
            <input
              type="password"
              name="currentPassword"
              value={passwordData.currentPassword}
              onChange={handleInputChange}
              required
              style={dashboardStyles.input}
            />
          </div>

          <div style={dashboardStyles.formGroup}>
            <label style={dashboardStyles.label}>New Password</label>
            <input
              type="password"
              name="newPassword"
              value={passwordData.newPassword}
              onChange={handleInputChange}
              required
              style={dashboardStyles.input}
            />
          </div>

          <div style={dashboardStyles.formGroup}>
            <label style={dashboardStyles.label}>Confirm New Password</label>
            <input
              type="password"
              name="confirmPassword"
              value={passwordData.confirmPassword}
              onChange={handleInputChange}
              required
              style={dashboardStyles.input}
            />
          </div>

          <div style={dashboardStyles.formActions}>
            <button type="submit" style={dashboardStyles.submitButton}>
              Change Password
            </button>
            <button 
              type="button" 
              style={dashboardStyles.cancelButton}
              onClick={() => setCurrentPage('dashboard')}
            >
              Back to Dashboard
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Profile Page Component
function ProfilePage({ isMobile, setCurrentPage, user, onLogout, onUserUpdate }) {
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    countryCode: '+263'
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/index.php`, {
        apiType: "updateProfile",
        ...profileData
      }, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        const updatedUser = {
          ...user,
          ...profileData
        };
        onUserUpdate(updatedUser);
        alert('Profile updated successfully!');
        setCurrentPage('dashboard');
      } else {
        alert('Failed to update profile. Please try again.');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      alert('Failed to update profile. Please try again.');
    }
  };

  return (
    <div style={dashboardStyles.page}>
      <Header 
        isMobile={isMobile} 
        currentPage="profile"
        setCurrentPage={setCurrentPage}
        cartItemCount={0}
        user={user}
        onLogout={onLogout}
      />
      
      <div style={dashboardStyles.container}>
        <div style={dashboardStyles.header}>
          <h1 style={dashboardStyles.title}>Edit Profile</h1>
          <p style={dashboardStyles.subtitle}>Update your personal information</p>
        </div>

        <div style={dashboardStyles.mainContent}>
          <div style={dashboardStyles.section}>
            <div style={dashboardStyles.formCard}>
              <form onSubmit={handleSubmit}>
                <div style={dashboardStyles.formGrid}>
                  <div style={dashboardStyles.formGroup}>
                    <label style={dashboardStyles.label}>Full Name</label>
                    <input
                      type="text"
                      name="name"
                      value={profileData.name}
                      onChange={handleInputChange}
                      required
                      style={dashboardStyles.input}
                    />
                  </div>

                  <div style={dashboardStyles.formGroup}>
                    <label style={dashboardStyles.label}>Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={profileData.email}
                      onChange={handleInputChange}
                      required
                      style={dashboardStyles.input}
                    />
                  </div>
                </div>

                <div style={dashboardStyles.formGroup}>
                  <label style={dashboardStyles.label}>Phone Number</label>
                  <div style={dashboardStyles.phoneInputContainer}>
                    <select 
                      name="countryCode"
                      value={profileData.countryCode}
                      onChange={handleInputChange}
                      style={dashboardStyles.countryCodeSelect}
                    >
                      {countryCodes.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.flag} {country.code}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      name="phone"
                      value={profileData.phone}
                      onChange={handleInputChange}
                      style={dashboardStyles.phoneInput}
                      placeholder="Phone number"
                    />
                  </div>
                </div>

                <div style={dashboardStyles.formGroup}>
                  <label style={dashboardStyles.label}>Address</label>
                  <textarea
                    name="address"
                    value={profileData.address}
                    onChange={handleInputChange}
                    rows="3"
                    style={dashboardStyles.textarea}
                    placeholder="Enter your full address"
                  />
                </div>

                <div style={dashboardStyles.formActions}>
                  <button type="submit" style={dashboardStyles.submitButton}>
                    Save Changes
                  </button>
                  <button 
                    type="button" 
                    style={dashboardStyles.cancelButton}
                    onClick={() => setCurrentPage('dashboard')}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Footer isMobile={isMobile} setCurrentPage={setCurrentPage} />
    </div>
  );
}

// Change Password Page Component
function ChangePasswordPage({ isMobile, setCurrentPage, user, onLogout }) {
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match!');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      alert('New password must be at least 6 characters long!');
      return;
    }
    alert('Password changed successfully!');
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setCurrentPage('dashboard');
  };

  return (
    <div style={dashboardStyles.page}>
      <Header 
        isMobile={isMobile} 
        currentPage="change-password"
        setCurrentPage={setCurrentPage}
        cartItemCount={0}
        user={user}
        onLogout={onLogout}
      />
      
      <div style={dashboardStyles.container}>
        <div style={dashboardStyles.header}>
          <h1 style={dashboardStyles.title}>Change Password</h1>
          <p style={dashboardStyles.subtitle}>Update your password for security</p>
        </div>

        <div style={dashboardStyles.mainContent}>
          <div style={dashboardStyles.section}>
            <div style={dashboardStyles.formCard}>
              <form onSubmit={handleSubmit}>
                <div style={dashboardStyles.formGroup}>
                  <label style={dashboardStyles.label}>Current Password</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handleInputChange}
                    required
                    style={dashboardStyles.input}
                  />
                </div>

                <div style={dashboardStyles.formGroup}>
                  <label style={dashboardStyles.label}>New Password</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handleInputChange}
                    required
                    style={dashboardStyles.input}
                  />
                  <small style={dashboardStyles.helpText}>
                    Password must be at least 6 characters long
                  </small>
                </div>

                <div style={dashboardStyles.formGroup}>
                  <label style={dashboardStyles.label}>Confirm New Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    style={dashboardStyles.input}
                  />
                </div>

                <div style={dashboardStyles.formActions}>
                  <button type="submit" style={dashboardStyles.submitButton}>
                    Change Password
                  </button>
                  <button 
                    type="button" 
                    style={dashboardStyles.cancelButton}
                    onClick={() => setCurrentPage('dashboard')}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Footer isMobile={isMobile} setCurrentPage={setCurrentPage} />
    </div>
  );
}

// Products Component
function Products({ isMobile, featuredProducts }) {
  return (
    <section id="products" style={styles.products}>
      <div style={styles.container}>
        <h2 style={{
          ...styles.sectionTitle,
          fontSize: isMobile ? '28px' : '42px'
        }}>Our Featured Products</h2>
        <div style={{
          ...styles.productsGrid,
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: isMobile ? '20px' : '35px'
        }}>
          {featuredProducts.map((product) => (
            <div key={product.id} style={styles.productCard}>
              <div style={styles.productImage}>
                <img 
                  src={product.image} 
                  alt={product.name} 
                  style={styles.productImg}
                />
              </div>
              <h3 style={styles.productName}>{product.name}</h3>
              <p style={styles.productDescription}>{product.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Shop Page Component
function ShopPage({ isMobile, addToCart, currentPage, setCurrentPage, cartItemCount, user, onLogout, products }) {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('name');

  const categories = ['All', ...new Set(products.filter(p => p.isActive).map(product => product.category))];

  const filteredProducts = products.filter(product => 
    (selectedCategory === 'All' || product.category === selectedCategory) && product.isActive
  );

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'name':
      default:
        return a.name.localeCompare(b.name);
    }
  });

  return (
    <div style={shopStyles.page}>
      <Header 
        isMobile={isMobile} 
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        cartItemCount={cartItemCount}
        user={user}
        onLogout={onLogout}
      />
      
      <div style={shopStyles.hero}>
        <div style={shopStyles.heroContent}>
          <h1 style={shopStyles.heroTitle}>Our Premium Pork Selection</h1>
          <p style={shopStyles.heroSubtitle}>Fresh, quality cuts delivered to your doorstep</p>
        </div>
      </div>

      <div style={shopStyles.container}>
        <div style={shopStyles.filters}>
          <div style={shopStyles.filterGroup}>
            <label style={shopStyles.filterLabel}>Category:</label>
            <select 
              value={selectedCategory} 
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={shopStyles.filterSelect}
            >
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          
          <div style={shopStyles.filterGroup}>
            <label style={shopStyles.filterLabel}>Sort by:</label>
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              style={shopStyles.filterSelect}
            >
              <option value="name">Name</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
            </select>
          </div>
        </div>

        <div style={{
          ...shopStyles.productsGrid,
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(350px, 1fr))'
        }}>
          {sortedProducts.map(product => (
            <ProductCard 
              key={product.id} 
              product={product} 
              isMobile={isMobile} 
              addToCart={addToCart}
            />
          ))}
        </div>

        {sortedProducts.length === 0 && (
          <div style={shopStyles.emptyState}>
            <p style={shopStyles.emptyText}>No products found in this category.</p>
          </div>
        )}
      </div>

      <Footer isMobile={isMobile} setCurrentPage={setCurrentPage} />
    </div>
  );
}

// Product Card Component
function ProductCard({ product, isMobile, addToCart }) {
  const [purchaseType, setPurchaseType] = useState('weight');
  const [quantity, setQuantity] = useState(1);
  const [customAmount, setCustomAmount] = useState('');
  const [customWeight, setCustomWeight] = useState('');

  const handleAddToCart = () => {
    if (purchaseType === 'amount') {
      if (!customAmount || parseFloat(customAmount) <= 0) {
        alert('Please enter a valid amount');
        return;
      }
      addToCart(product, 1, 'amount', parseFloat(customAmount));
    } else if (purchaseType === 'customWeight') {
      if (!customWeight || parseFloat(customWeight) <= 0) {
        alert('Please enter a valid weight');
        return;
      }
      addToCart(product, parseFloat(customWeight), 'weight');
    } else {
      addToCart(product, quantity, 'weight');
    }
    
    setQuantity(1);
    setCustomAmount('');
    setCustomWeight('');
    alert('Added to cart!');
  };

  const calculatedWeight = customAmount ? (customAmount / product.price).toFixed(2) : null;
  const calculatedAmount = customWeight ? (customWeight * product.price).toFixed(2) : null;

  return (
    <div style={shopStyles.productCard}>
      <div style={shopStyles.productImage}>
        <img src={product.image} alt={product.name} style={shopStyles.productImg} />
        <div style={shopStyles.productCategory}>
          {product.isWholesale ? '📦 ' : ''}{product.category}
        </div>
        {product.isWholesale && (
          <div style={shopStyles.wholesaleBadge}>WHOLESALE</div>
        )}
        {!product.isActive && (
          <div style={shopStyles.inactiveBadge}>UNAVAILABLE</div>
        )}
      </div>
      
      <div style={shopStyles.productInfo}>
        <h3 style={shopStyles.productName}>{product.name}</h3>
        <p style={shopStyles.productDescription}>{product.description}</p>
        
        <div style={shopStyles.productDetails}>
          <div style={shopStyles.productWeight}>
            {product.isWholesale ? 'Min. ' : ''}{product.weight}
          </div>
          <div style={shopStyles.productPrice}>${product.price.toFixed(2)}/kg</div>
        </div>

        {product.isActive && (
          <div style={shopStyles.purchaseOptions}>
            <div style={shopStyles.purchaseTypeSelector}>
              <label style={shopStyles.radioLabel}>
                <input
                  type="radio"
                  value="weight"
                  checked={purchaseType === 'weight'}
                  onChange={(e) => setPurchaseType(e.target.value)}
                  style={shopStyles.radioInput}
                />
                By Weight ({product.weight})
              </label>
              
              <label style={shopStyles.radioLabel}>
                <input
                  type="radio"
                  value="customWeight"
                  checked={purchaseType === 'customWeight'}
                  onChange={(e) => setPurchaseType(e.target.value)}
                  style={shopStyles.radioInput}
                />
                Custom Weight
              </label>
              
              <label style={shopStyles.radioLabel}>
                <input
                  type="radio"
                  value="amount"
                  checked={purchaseType === 'amount'}
                  onChange={(e) => setPurchaseType(e.target.value)}
                  style={shopStyles.radioInput}
                />
                By Amount ($)
              </label>
            </div>

            {purchaseType === 'weight' && (
              <div style={shopStyles.quantitySelector}>
                <label style={shopStyles.quantityLabel}>Quantity:</label>
                <div style={shopStyles.quantityControls}>
                  <button 
                    style={shopStyles.quantityButton}
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    style={shopStyles.quantityInput}
                    min="1"
                  />
                  <button 
                    style={shopStyles.quantityButton}
                    onClick={() => setQuantity(quantity + 1)}
                  >
                    +
                  </button>
                </div>
                <div style={shopStyles.calculation}>
                  Total: ${(product.price * quantity).toFixed(2)}
                </div>
              </div>
            )}

            {purchaseType === 'customWeight' && (
              <div style={shopStyles.customInput}>
                <label style={shopStyles.inputLabel}>Weight (kg):</label>
                <input
                  type="number"
                  value={customWeight}
                  onChange={(e) => setCustomWeight(e.target.value)}
                  placeholder="e.g., 1.5"
                  step="0.1"
                  min="0.1"
                  style={shopStyles.textInput}
                />
                {calculatedAmount && (
                  <div style={shopStyles.calculation}>
                    Amount: ${calculatedAmount}
                  </div>
                )}
              </div>
            )}

            {purchaseType === 'amount' && (
              <div style={shopStyles.customInput}>
                <label style={shopStyles.inputLabel}>Amount ($):</label>
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="e.g., 30.00"
                  step="0.01"
                  min="0.01"
                  style={shopStyles.textInput}
                />
                {calculatedWeight && (
                  <div style={shopStyles.calculation}>
                    Weight: {calculatedWeight} kg
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        <button 
          style={{
            ...shopStyles.addToCartButton,
            backgroundColor: !product.isActive ? '#999' : product.isWholesale ? '#2d5016' : '#1a1a1a',
            cursor: !product.isActive ? 'not-allowed' : 'pointer'
          }}
          onClick={handleAddToCart}
          disabled={!product.isActive}
        >
          {!product.isActive ? 'Out of Stock' : product.isWholesale ? 'Add Wholesale to Cart' : 'Add to Cart'}
        </button>
      </div>
    </div>
  );
}

// Checkout Page Component
function CheckoutPage({ isMobile, cart, removeFromCart, updateQuantity, getCartTotal, currentPage, setCurrentPage, clearCart, user }) {
  const [customerInfo, setCustomerInfo] = useState({
    name: user?.name || '',
    phone: '',
    countryCode: '+263',
    email: user?.email || '',
    address: '',
    city: 'Chitungwiza',
    deliveryInstructions: '',
    paymentMethod: 'cash'
  });

  const [deliveryOption, setDeliveryOption] = useState('delivery');
  const [deliveryFee, setDeliveryFee] = useState(5.00);
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setCustomerInfo(prev => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email
      }));
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDeliveryOptionChange = (option) => {
    setDeliveryOption(option);
    if (option === 'pickup') {
      setDeliveryFee(0);
    } else {
      setDeliveryFee(5.00);
    }
  };

  const calculateDeliveryFee = () => {
    if (deliveryOption === 'pickup') {
      return 0;
    }
    
    switch (customerInfo.city.toLowerCase()) {
      case 'harare':
        return 10.00;
      case 'chitungwiza':
        return 5.00;
      default:
        return 15.00;
    }
  };

  useEffect(() => {
    setDeliveryFee(calculateDeliveryFee());
  }, [deliveryOption, customerInfo.city]);

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    
    if (cart.length === 0) {
      alert('Your cart is empty!');
      return;
    }
    //console.log("Cart Data = ", cart);

    setIsSubmitting(true);

    const orderData = {
      customerInfo: {
        ...customerInfo,
        fullPhone: `${customerInfo.countryCode} ${customerInfo.phone}`,
        userId: user?.id || null
      },
      cart,
      subtotal: getCartTotal(),
      deliveryFee,
      total: getCartTotal() + deliveryFee,
      deliveryOption,
      paymentMethod: customerInfo.paymentMethod
    };

    try {
      const response = await axios.post(
        `${API_URL}/index.php`,
        {
          apiType: "submitOrder",
          ...orderData
        }
      );

      if (response.data.success) {
        setOrderSubmitted(true);
        clearCart();
        alert('Order submitted successfully!');
      } else {
        alert(response.data.message || 'Failed to submit order. Please try again.');
      }
    } catch (error) {
      console.error("Error posting order:", error);
      alert("Error submitting order. Please check your internet connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (orderSubmitted) {
    return (
      <div style={checkoutStyles.page}>
        <Header 
          isMobile={isMobile} 
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          cartItemCount={0}
        />
        
        <div style={checkoutStyles.container}>
          <div style={checkoutStyles.successMessage}>
            <div style={checkoutStyles.successIcon}>✓</div>
            <h2 style={checkoutStyles.successTitle}>Order Confirmed!</h2>
            <p style={checkoutStyles.successText}>
              Thank you for your order, {customerInfo.name}! Your order has been received and will be processed shortly.
            </p>
            <p style={checkoutStyles.successDetails}>
              We'll contact you at {customerInfo.countryCode} {customerInfo.phone} to confirm {deliveryOption === 'pickup' ? 'pickup' : 'delivery'} details.
            </p>
            <button 
              style={checkoutStyles.continueShopping}
              onClick={() => setCurrentPage('shop')}
            >
              Continue Shopping
            </button>
          </div>
        </div>

        <Footer isMobile={isMobile} setCurrentPage={setCurrentPage} />
      </div>
    );
  }

  return (
    <div style={checkoutStyles.page}>
      <Header 
        isMobile={isMobile} 
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        cartItemCount={cart.reduce((total, item) => total + (item.purchaseType === 'amount' ? 1 : item.quantity), 0)}
      />
      
      <div style={checkoutStyles.container}>
        <h1 style={checkoutStyles.title}>Checkout</h1>
        
        {!user && (
          <div style={checkoutStyles.authPrompt}>
            <p>Have an account? 
              <button 
                style={checkoutStyles.authLink}
                onClick={() => setCurrentPage('login')}
              >
                Sign in
              </button>
              for a faster checkout experience!
            </p>
          </div>
        )}
        
        <div style={{
          ...checkoutStyles.content,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <div style={checkoutStyles.orderSummary}>
            <h2 style={checkoutStyles.sectionTitle}>Order Summary</h2>
            
            {cart.length === 0 ? (
              <div style={checkoutStyles.emptyCart}>
                <p>Your cart is empty</p>
                <button 
                  style={checkoutStyles.shopButton}
                  onClick={() => setCurrentPage('shop')}
                >
                  Start Shopping
                </button>
              </div>
            ) : (
              <>
                <div style={checkoutStyles.cartItems}>
                  {cart.map((item, index) => (
                    <div key={`${item.id}-${index}`} style={checkoutStyles.cartItem}>
                      <img 
                        src={item.image} 
                        alt={item.name} 
                        style={checkoutStyles.cartItemImage}
                      />
                      <div style={checkoutStyles.cartItemDetails}>
                        <h4 style={checkoutStyles.cartItemName}>{item.name}</h4>
                        <p style={checkoutStyles.cartItemPrice}>
                          {item.purchaseType === 'amount' ? (
                            <>${parseFloat(item.customAmount).toFixed(2)}</>
                          ) : (
                            <>${item.price.toFixed(2)}/kg</>
                          )}
                        </p>
                        {item.purchaseType === 'amount' && (
                          <p style={checkoutStyles.cartItemWeight}>
                            Weight: {item.calculatedWeight} kg
                          </p>
                        )}
                        {item.purchaseType === 'weight' && item.quantity !== 1 && (
                          <p style={checkoutStyles.cartItemWeight}>
                            Weight: {item.quantity} kg
                          </p>
                        )}
                      </div>
                      <div style={checkoutStyles.quantityControls}>
                        {item.purchaseType !== 'amount' && (
                          <>
                            <button 
                              style={checkoutStyles.quantityButton}
                              onClick={() => {
                                if (item.purchaseType === 'customWeight') {
                                  updateQuantity(item.id, Math.max(0.1, item.quantity - 0.1));
                                } else {
                                  updateQuantity(item.id, item.quantity - 1);
                                }
                              }}
                            >
                              -
                            </button>
                            <span style={checkoutStyles.quantity}>
                              {item.purchaseType === 'customWeight' ? item.quantity.toFixed(1) : item.quantity}
                              {item.purchaseType === 'customWeight' && 'kg'}
                            </span>
                            <button 
                              style={checkoutStyles.quantityButton}
                              onClick={() => {
                                if (item.purchaseType === 'customWeight') {
                                  updateQuantity(item.id, item.quantity + 0.1);
                                } else {
                                  updateQuantity(item.id, item.quantity + 1);
                                }
                              }}
                            >
                              +
                            </button>
                          </>
                        )}
                      </div>
                      <div style={checkoutStyles.cartItemTotal}>
                        {item.purchaseType === 'amount' ? (
                          <>${parseFloat(item.customAmount).toFixed(2)}</>
                        ) : (
                          <>${(item.price * item.quantity).toFixed(2)}</>
                        )}
                      </div>
                      <button 
                        style={checkoutStyles.removeButton}
                        onClick={() => removeFromCart(item.id)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                
                <div style={checkoutStyles.orderTotal}>
                  <div style={checkoutStyles.totalRow}>
                    <span>Subtotal:</span>
                    <span>${getCartTotal().toFixed(2)}</span>
                  </div>
                  <div style={checkoutStyles.totalRow}>
                    <span>Delivery:</span>
                    <span>${deliveryFee.toFixed(2)}</span>
                  </div>
                  <div style={checkoutStyles.totalRow}>
                    <span style={checkoutStyles.grandTotal}>Total:</span>
                    <span style={checkoutStyles.grandTotal}>
                      ${(getCartTotal() + deliveryFee).toFixed(2)}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>

          {cart.length > 0 && (
            <div style={checkoutStyles.customerInfo}>
              <h2 style={checkoutStyles.sectionTitle}>Customer Information</h2>
              
              <form onSubmit={handleSubmitOrder} style={checkoutStyles.form}>
                <div style={checkoutStyles.formGroup}>
                  <label style={checkoutStyles.label}>Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={customerInfo.name}
                    onChange={handleInputChange}
                    required
                    style={checkoutStyles.input}
                  />
                </div>
                
                <div style={checkoutStyles.formGroup}>
                  <label style={checkoutStyles.label}>Phone Number *</label>
                  <div style={checkoutStyles.phoneInputContainer}>
                    <select 
                      name="countryCode"
                      value={customerInfo.countryCode}
                      onChange={handleInputChange}
                      style={checkoutStyles.countryCodeSelect}
                    >
                      {countryCodes.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.flag} {country.code}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      name="phone"
                      value={customerInfo.phone}
                      onChange={handleInputChange}
                      required
                      style={checkoutStyles.phoneInput}
                      placeholder="Phone number"
                    />
                  </div>
                </div>
                
                <div style={checkoutStyles.formGroup}>
                  <label style={checkoutStyles.label}>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={customerInfo.email}
                    onChange={handleInputChange}
                    style={checkoutStyles.input}
                  />
                </div>

                <div style={checkoutStyles.formGroup}>
                  <label style={checkoutStyles.label}>Delivery Option</label>
                  <div style={checkoutStyles.deliveryOptions}>
                    <label style={checkoutStyles.radioLabel}>
                      <input
                        type="radio"
                        value="delivery"
                        checked={deliveryOption === 'delivery'}
                        onChange={() => handleDeliveryOptionChange('delivery')}
                        style={checkoutStyles.radioInput}
                      />
                      Delivery (${deliveryFee.toFixed(2)})
                    </label>
                    <label style={checkoutStyles.radioLabel}>
                      <input
                        type="radio"
                        value="pickup"
                        checked={deliveryOption === 'pickup'}
                        onChange={() => handleDeliveryOptionChange('pickup')}
                        style={checkoutStyles.radioInput}
                      />
                      Pickup (Free) - Zvido Business Centre
                    </label>
                  </div>
                </div>
                
                {deliveryOption === 'delivery' && (
                  <>
                    <div style={checkoutStyles.formGroup}>
                      <label style={checkoutStyles.label}>Delivery Address *</label>
                      <textarea
                        name="address"
                        value={customerInfo.address}
                        onChange={handleInputChange}
                        required
                        rows="3"
                        style={checkoutStyles.textarea}
                        placeholder="Full street address, house number, etc."
                      />
                    </div>
                    
                    <div style={checkoutStyles.formGroup}>
                      <label style={checkoutStyles.label}>City/Area *</label>
                      <input
                        type="text"
                        name="city"
                        value={customerInfo.city}
                        onChange={handleInputChange}
                        required
                        style={checkoutStyles.input}
                        placeholder="e.g., Chitungwiza, Harare, etc."
                      />
                    </div>

                    <div style={checkoutStyles.formGroup}>
                      <label style={checkoutStyles.label}>Delivery Instructions</label>
                      <textarea
                        name="deliveryInstructions"
                        value={customerInfo.deliveryInstructions}
                        onChange={handleInputChange}
                        rows="2"
                        style={checkoutStyles.textarea}
                        placeholder="Any specific delivery instructions, landmarks, etc."
                      />
                    </div>
                  </>
                )}
                
                <div style={checkoutStyles.formGroup}>
                  <label style={checkoutStyles.label}>Payment Method</label>
                  <select
                    name="paymentMethod"
                    value={customerInfo.paymentMethod}
                    onChange={handleInputChange}
                    style={checkoutStyles.select}
                  >
                    <option value="cash">Cash on Delivery</option>
                    <option value="mobile">Mobile Money</option>
                    <option value="card">Debit/Credit Card</option>
                    <option value="transfer">Bank Transfer</option>
                  </select>
                </div>

                {customerInfo.paymentMethod === 'transfer' && (
                  <div style={checkoutStyles.bankDetails}>
                    <h4 style={checkoutStyles.bankTitle}>Bank Transfer Details</h4>
                    <p style={checkoutStyles.bankInfo}>
                      <strong>Bank:</strong> Your Bank Name<br/>
                      <strong>Account Name:</strong> Sir Henry's Butchery<br/>
                      <strong>Account Number:</strong> 1234567890<br/>
                      <strong>Branch Code:</strong> 12345
                    </p>
                    <p style={checkoutStyles.bankNote}>
                      Please use your order number as reference when transferring.
                    </p>
                  </div>
                )}
                
                <button 
                  type="submit" 
                  style={{
                    ...checkoutStyles.submitButton,
                    opacity: isSubmitting ? 0.7 : 1,
                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                  }}
                  disabled={isSubmitting || cart.length === 0}
                >
                  {isSubmitting ? 'Submitting Order...' : `Place Order : $${(getCartTotal() + deliveryFee).toFixed(2)}`}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>

      <Footer isMobile={isMobile} setCurrentPage={setCurrentPage} />
    </div>
  );
}

// Contact Component
function Contact({ isMobile }) {
  const [contactInfo, setContactInfo] = useState({
    name: '',
    phone: '',
    countryCode: '+263',
    email: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(
        `${API_URL}/index.php`,
        {
          apiType: "submitContactInfo",
          contactInfo: {
            ...contactInfo,
            fullPhone: `${contactInfo.countryCode} ${contactInfo.phone}`
          }
        }
      );

      if (response.data.success) {
        alert(response.data.message);
        setContactInfo({
          name: '',
          phone: '',
          countryCode: '+263',
          email: '',
          message: ''
        });
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error("Error posting contact:", error);
      alert("Error sending message. Please check your internet connection.");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <section id="contact" style={styles.contact}>
      <div style={styles.container}>
        <h2 style={{
          ...styles.sectionTitle,
          fontSize: isMobile ? '28px' : '42px'
        }}>Contact Us Today!</h2>
        <div style={{
          ...styles.contactContent,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '30px' : '60px'
        }}>
          <div style={styles.contactInfo}>
            <h3 style={styles.contactHeading}>Get In Touch</h3>
            
            <div style={styles.contactItem}>
              <h4 style={styles.contactSubHeading}>📍 Location</h4>
              <p style={styles.contactText}>
                Zvido Business Centre<br />
                Chitungwiza
              </p>
            </div>
            
            <div style={styles.contactItem}>
              <h4 style={styles.contactSubHeading}>📞 Phone</h4>
              <p style={styles.contactText}>+263 783 90 7172</p>
            </div>

            <div style={styles.contactItem}>
              <h4 style={styles.contactSubHeading}>📧 Email</h4>
              <p style={styles.contactText}>sirhenrysbutchery@gmail.com</p>
            </div>
            
            <div style={styles.contactItem}>
              <h4 style={styles.contactSubHeading}>🕒 Operating Hours</h4>
              <p style={styles.contactText}>Daily: 8am - 8pm</p>
            </div>
          </div>
          
          <div style={styles.contactForm}>
            <h3 style={styles.contactHeading}>Send Us a Message</h3>
            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.formGroup}>
                <label htmlFor="name" style={styles.formLabel}>Your Name</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name"
                  value={contactInfo.name}
                  onChange={handleInputChange}
                  required 
                  style={styles.formInput}
                />
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="phone" style={styles.formLabel}>Phone Number</label>
                <div style={styles.phoneInputContainer}>
                  <select 
                    name="countryCode"
                    value={contactInfo.countryCode}
                    onChange={handleInputChange}
                    style={styles.countryCodeSelect}
                  >
                    {countryCodes.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.code}
                      </option>
                    ))}
                  </select>
                  <input 
                    type="tel" 
                    id="phone" 
                    name="phone"
                    value={contactInfo.phone}
                    onChange={handleInputChange}
                    required 
                    style={styles.phoneInput}
                    placeholder="Phone number"
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="email" style={styles.formLabel}>Email</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email"
                  value={contactInfo.email}
                  onChange={handleInputChange}
                  style={styles.formInput}
                />
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="message" style={styles.formLabel}>Your Message</label>
                <textarea 
                  id="message" 
                  name="message" 
                  value={contactInfo.message}
                  onChange={handleInputChange}
                  rows="4" 
                  required 
                  style={styles.formTextarea}
                ></textarea>
              </div>
              <button 
                type="submit" 
                style={{
                  ...styles.submitButton,
                  opacity: isSubmitting ? 0.7 : 1,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer'
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

// Hero, About, WhyChooseUs, Testimonials, Footer components remain the same...
// (Keeping them the same as in your original code to save space)

function Hero({ isMobile, setCurrentPage }) {
  return (
    <section id="home" style={styles.hero}>
      <div style={styles.heroOverlay}>
        <div style={styles.heroContent}>
          <h2 style={{
            ...styles.heroTitle,
            fontSize: isMobile ? '32px' : '52px'
          }}>Quality You Can Taste</h2>
          <p style={{
            ...styles.heroSubtitle,
            fontSize: isMobile ? '18px' : '24px'
          }}>Delivering Fresh Quality Pork to All</p>
          <button 
            style={styles.ctaButton}
            onClick={() => setCurrentPage('shop')}
          >
            Shop Now
          </button>
        </div>
      </div>
    </section>
  );
}

function About({ isMobile }) {
  return (
    <section id="about" style={styles.about}>
      <div style={styles.container}>
        <div style={{
          ...styles.aboutContent,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '30px' : '60px'
        }}>
          <div style={styles.aboutText}>
            <h2 style={{
              ...styles.sectionTitle,
              fontSize: isMobile ? '28px' : '42px'
            }}>Pork Specialists</h2>
            <h3 style={styles.aboutHeading}>Our Mission</h3>
            <p style={styles.aboutParagraph}>
              Sir Henry's Butchery is dedicated to providing fresh quality pork meat at competitive prices. 
              Based in Zvido Business Centre, we specialize in various pork cuts to serve both restaurants 
              and individual customers, ensuring satisfaction for all.
            </p>
            
            <div style={styles.infoBox}>
              <div style={styles.infoItem}>
                <h4 style={styles.infoTitle}>Operating Hours</h4>
                <p style={styles.infoText}>We operate daily from 8 am to 8 pm.</p>
              </div>
              
              <div style={styles.infoItem}>
                <h4 style={styles.infoTitle}>Location</h4>
                <p style={styles.infoText}>
                  Zvido Business Centre<br />
                  Chitungwiza
                </p>
              </div>
              
              <div style={styles.infoItem}>
                <h4 style={styles.infoTitle}>Contact</h4>
                <p style={styles.infoText}>+263 783 90 7172</p>
                <p style={styles.infoText}>sirhenrysbutchery@gmail.com</p>
              </div>
            </div>
          </div>
          <div style={styles.aboutImage}>
            <img 
              src="https://images.unsplash.com/photo-1544025162-d76694265947?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTB8fHBvcmslMjBoZWFkfGVufDB8fDB8fHww&auto=format&fit=crop&w=800&q=60"
              alt="Fresh pork selection" 
              style={styles.aboutImg}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyChooseUs({ isMobile }) {
  return (
    <section style={styles.whyChooseUs}>
      <div style={styles.container}>
        <h2 style={{
          ...styles.sectionTitle,
          fontSize: isMobile ? '28px' : '42px'
        }}>Why Choose Sir Henry's?</h2>
        <div style={{
          ...styles.benefitsGrid,
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: isMobile ? '20px' : '35px'
        }}>
          <div style={styles.benefitCard}>
            <h3 style={styles.benefitTitle}>Competitive Wholesale Prices</h3>
            <p style={styles.benefitText}>We offer the best market prices without compromising on quality</p>
          </div>
          
          <div style={styles.benefitCard}>
            <h3 style={styles.benefitTitle}>Fresh & Hygienically Handled</h3>
            <p style={styles.benefitText}>Our meats are handled with the highest standards of hygiene</p>
          </div>
          
          <div style={styles.benefitCard}>
            <h3 style={styles.benefitTitle}>Consistent Top Quality</h3>
            <p style={styles.benefitText}>We maintain premium quality in every cut, every time</p>
          </div>

          <div style={styles.benefitCard}>
            <h3 style={styles.benefitTitle}>Farm to Butchery Guarantee</h3>
            <p style={styles.benefitText}>Guaranteed quality from Sir Henry Farm to Sir Henry Butchery!</p>
          </div>
        </div>
        
        <div style={styles.qualitySlogan}>
          <h3 style={styles.sloganText}>"Quality Meat, Every Time, Every Bite"</h3>
        </div>
      </div>
    </section>
  );
}

function Testimonials({ isMobile }) {
  const testimonials = [
    {
      text: "The pork from Sir Henry's is always fresh and of excellent quality. I've been a loyal customer for years!",
      name: "John M.",
      role: "Restaurant Owner"
    },
    {
      text: "Best prices for wholesale pork in Chitungwiza. Their service is reliable and the meat is always top-notch.",
      name: "Sarah T.",
      role: "Catering Business"
    },
    {
      text: "I appreciate their flexible pricing for loyal customers. The quality is consistent and the staff is friendly.",
      name: "David K.",
      role: "Regular Customer"
    }
  ];

  return (
    <section id="testimonials" style={styles.testimonials}>
      <div style={styles.container}>
        <h2 style={{
          ...styles.sectionTitle,
          fontSize: isMobile ? '28px' : '42px'
        }}>What Our Customers Say</h2>
        <div style={{
          ...styles.testimonialsGrid,
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: isMobile ? '20px' : '35px'
        }}>
          {testimonials.map((testimonial, index) => (
            <div key={index} style={styles.testimonialCard}>
              <div style={styles.testimonialText}>
                <p style={styles.testimonialQuote}>"{testimonial.text}"</p>
              </div>
              <div style={styles.customerInfo}>
                <h4 style={styles.customerName}>{testimonial.name}</h4>
                <p style={styles.customerRole}>{testimonial.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer({ isMobile, setCurrentPage }) {
  return (
    <footer style={styles.footer}>
      <div style={styles.container}>
        <div style={{
          ...styles.footerContent,
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: isMobile ? '30px' : '50px'
        }}>
          <div style={styles.footerSection}>
            <h3 style={styles.footerLogo}>Sir Henry's Butchery</h3>
            <p style={styles.footerTagline}>Quality You Can Taste</p>
            <p style={styles.footerText}>Providing premium pork cuts in Chitungwiza</p>
          </div>
          
          <div style={styles.footerSection}>
            <h4 style={styles.footerHeading}>Quick Links</h4>
            <ul style={styles.footerList}>
              <li style={styles.footerListItem}>
                <a 
                  href="#home" 
                  style={styles.footerLink}
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentPage('home');
                  }}
                >
                  Home
                </a>
              </li>
              <li style={styles.footerListItem}>
                <a 
                  href="#shop" 
                  style={styles.footerLink}
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentPage('shop');
                  }}
                >
                  Shop
                </a>
              </li>
              <li style={styles.footerListItem}>
                <a 
                  href="#about" 
                  style={styles.footerLink}
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentPage('home');
                  }}
                >
                  About
                </a>
              </li>
              <li style={styles.footerListItem}>
                <a 
                  href="#contact" 
                  style={styles.footerLink}
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentPage('home');
                  }}
                >
                  Contact
                </a>
              </li>
            </ul>
          </div>
          
          <div style={styles.footerSection}>
            <h4 style={styles.footerHeading}>Contact Info</h4>
            <p style={styles.footerText}>+263 783 90 7172</p>
            <p style={styles.footerText}>sirhenrysbutchery@gmail.com</p>
            <p style={styles.footerText}>Zvido Business Centre, Chitungwiza</p>
            <p style={styles.footerText}>Daily: 8am - 8pm</p>
          </div>
        </div>
        
        <div style={styles.footerBottom}>
          <p style={styles.footerBottomText}>
            &copy; {new Date().getFullYear()} Sir Henry's Butchery. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

// Styles remain exactly the same as in your original code...
// (Keeping all the style objects unchanged)

// Dashboard Styles
const dashboardStyles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa'
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
    width: '100%',
    boxSizing: 'border-box'
  },
  header: {
    textAlign: 'center',
    marginBottom: '40px',
    padding: '20px 0'
  },
  title: {
    fontSize: '36px',
    fontWeight: '800',
    marginBottom: '10px',
    color: '#1a1a1a',
    background: 'linear-gradient(45deg, #1a1a1a, #d4af37)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  },
  subtitle: {
    fontSize: '18px',
    color: '#666',
    margin: 0
  },
  content: {
    display: 'flex',
    gap: '30px',
    minHeight: '600px'
  },
  sidebar: {
    width: isMobile => isMobile ? '100%' : '280px',
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column'
  },
  nav: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1
  },
  navButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '15px 20px',
    border: 'none',
    background: 'transparent',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: '16px',
    textAlign: 'left'
  },
  navButtonActive: {
    background: 'linear-gradient(45deg, #d4af37, #f4d03f)',
    color: '#1a1a1a',
    fontWeight: '600'
  },
  navIcon: {
    fontSize: '20px',
    width: '24px'
  },
  navText: {
    flex: 1
  },
  sidebarFooter: {
    marginTop: 'auto',
    paddingTop: '20px',
    borderTop: '1px solid #e0e0e0'
  },
  userRole: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '15px',
    textAlign: 'center'
  },
  backToShop: {
    width: '100%',
    background: 'transparent',
    border: '1px solid #d4af37',
    color: '#d4af37',
    padding: '10px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease'
  },
  mainContent: {
    flex: 1,
    background: 'white',
    borderRadius: '12px',
    padding: '30px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    overflow: 'auto'
  },
  overview: {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px'
  },
  section: {
    marginBottom: '30px'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  sectionTitle: {
    fontSize: '24px',
    fontWeight: '700',
    marginBottom: '20px',
    color: '#1a1a1a'
  },
  subSection: {
    marginBottom: '30px',
    padding: '20px',
    background: '#f8f9fa',
    borderRadius: '8px'
  },
  subSectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '15px',
    color: '#1a1a1a'
  },
  statsGrid: {
    display: 'grid',
    gap: '20px',
    marginBottom: '30px'
  },
  statCard: {
    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    padding: '25px',
    borderRadius: '12px',
    textAlign: 'center',
    borderLeft: '5px solid #d4af37'
  },
  statValue: {
    fontSize: '32px',
    fontWeight: '800',
    marginBottom: '8px',
    color: '#1a1a1a'
  },
  statLabel: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '8px'
  },
  statChange: {
    fontSize: '12px',
    color: '#4CAF50',
    fontWeight: '600'
  },
  activityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  activityItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    padding: '15px',
    background: '#f8f9fa',
    borderRadius: '8px'
  },
  activityIcon: {
    fontSize: '20px',
    width: '24px'
  },
  activityContent: {
    flex: 1
  },
  activityText: {
    margin: '0 0 5px 0',
    fontSize: '14px',
    color: '#1a1a1a'
  },
  activityTime: {
    fontSize: '12px',
    color: '#666'
  },
  actionsGrid: {
    display: 'grid',
    gridTemplateColumns: isMobile => isMobile ? '1fr' : 'repeat(2, 1fr)',
    gap: '15px'
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '15px 20px',
    background: 'linear-gradient(45deg, #f8f9fa, #e9ecef)',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: '14px',
    fontWeight: '500'
  },
  actionIcon: {
    fontSize: '18px'
  },
  addButton: {
    background: 'linear-gradient(45deg, #d4af37, #f4d03f)',
    color: '#1a1a1a',
    border: 'none',
    padding: '12px 20px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  formOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px'
  },
  formCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '30px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto'
  },
  formTitle: {
    fontSize: '20px',
    fontWeight: '700',
    marginBottom: '20px',
    color: '#1a1a1a'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '15px',
    marginBottom: '15px'
  },
  formGroup: {
    marginBottom: '15px'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#1a1a1a',
    fontSize: '14px'
  },
  input: {
    width: '100%',
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    resize: 'vertical',
    minHeight: '80px',
    fontFamily: 'inherit',
    boxSizing: 'border-box'
  },
  phoneInputContainer: {
    display: 'flex',
    gap: '10px'
  },
  countryCodeSelect: {
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: 'white',
    minWidth: '120px'
  },
  phoneInput: {
    flex: 1,
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px'
  },
  checkboxGroup: {
    marginBottom: '20px'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#555',
    cursor: 'pointer'
  },
  checkbox: {
    margin: 0
  },
  formActions: {
    display: 'flex',
    gap: '15px',
    marginTop: '25px'
  },
  submitButton: {
    background: 'linear-gradient(45deg, #d4af37, #f4d03f)',
    color: '#1a1a1a',
    border: 'none',
    padding: '12px 25px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    flex: 1
  },
  cancelButton: {
    background: 'transparent',
    border: '1px solid #ddd',
    color: '#666',
    padding: '12px 25px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    flex: 1
  },
  primaryButton: {
    background: 'linear-gradient(45deg, #d4af37, #f4d03f)',
    color: '#1a1a1a',
    border: 'none',
    padding: '12px 25px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  secondaryButton: {
    background: 'transparent',
    border: '1px solid #d4af37',
    color: '#d4af37',
    padding: '12px 25px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  productsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  productItem: {
    display: 'flex',
    gap: '15px',
    padding: '20px',
    background: '#f8f9fa',
    borderRadius: '8px',
    transition: 'all 0.3s ease'
  },
  productImage: {
    width: '80px',
    height: '80px',
    objectFit: 'cover',
    borderRadius: '8px'
  },
  productInfo: {
    flex: 1
  },
  productName: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '5px',
    color: '#1a1a1a'
  },
  productDescription: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '10px',
    lineHeight: '1.4'
  },
  productDetails: {
    display: 'flex',
    gap: '15px',
    fontSize: '12px',
    color: '#666'
  },
  productPrice: {
    fontWeight: '600',
    color: '#d4af37'
  },
  productWeight: {
    background: '#e9ecef',
    padding: '2px 8px',
    borderRadius: '12px'
  },
  productCategory: {
    background: '#e9ecef',
    padding: '2px 8px',
    borderRadius: '12px'
  },
  productStatus: {
    fontWeight: '600'
  },
  productActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    minWidth: '100px'
  },
  editButton: {
    background: '#2196f3',
    color: 'white',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500'
  },
  toggleButton: {
    background: '#ff9800',
    color: 'white',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500'
  },
  deleteButton: {
    background: '#f44336',
    color: 'white',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500'
  },
  featuredList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  featuredItem: {
    display: 'flex',
    gap: '15px',
    padding: '15px',
    background: 'white',
    borderRadius: '8px',
    border: '1px solid #e0e0e0'
  },
  featuredImage: {
    width: '60px',
    height: '60px',
    objectFit: 'cover',
    borderRadius: '8px'
  },
  featuredInfo: {
    flex: 1
  },
  featuredName: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '5px',
    color: '#1a1a1a'
  },
  featuredDescription: {
    fontSize: '12px',
    color: '#666',
    lineHeight: '1.4'
  },
  featuredActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  moveButton: {
    background: '#e9ecef',
    border: 'none',
    padding: '5px 10px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px'
  },
  removeButton: {
    background: '#f44336',
    color: 'white',
    border: 'none',
    padding: '5px 10px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px'
  },
  availableProducts: {
    display: 'grid',
    gridTemplateColumns: isMobile => isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '15px'
  },
  availableItem: {
    display: 'flex',
    flexDirection: 'column',
    padding: '15px',
    background: 'white',
    borderRadius: '8px',
    border: '1px solid #e0e0e0'
  },
  availableImage: {
    width: '100%',
    height: '120px',
    objectFit: 'cover',
    borderRadius: '8px',
    marginBottom: '10px'
  },
  availableInfo: {
    flex: 1,
    marginBottom: '10px'
  },
  availableName: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '5px',
    color: '#1a1a1a'
  },
  availableDescription: {
    fontSize: '12px',
    color: '#666',
    marginBottom: '8px',
    lineHeight: '1.4'
  },
  availablePrice: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#d4af37'
  },
  addFeaturedButton: {
    background: 'linear-gradient(45deg, #d4af37, #f4d03f)',
    color: '#1a1a1a',
    border: 'none',
    padding: '8px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600'
  },
  ordersGrid: {
    display: 'grid',
    gridTemplateColumns: isMobile => isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px'
  },
  orderCard: {
    background: '#f8f9fa',
    padding: '20px',
    borderRadius: '8px',
    borderLeft: '5px solid #d4af37'
  },
  orderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px'
  },
  orderId: {
    fontSize: '16px',
    fontWeight: '600',
    margin: 0,
    color: '#1a1a1a'
  },
  orderStatus: {
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'white'
  },
  orderDetails: {
    marginBottom: '15px'
  },
  orderCustomer: {
    margin: '0 0 5px 0',
    fontSize: '14px'
  },
  orderTotal: {
    margin: '0 0 5px 0',
    fontSize: '14px'
  },
  orderDate: {
    margin: 0,
    fontSize: '14px',
    color: '#666'
  },
  orderActions: {
    display: 'flex',
    gap: '10px'
  },
  viewButton: {
    background: '#2196f3',
    color: 'white',
    border: 'none',
    padding: '8px 15px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500'
  },
  updateButton: {
    background: '#ff9800',
    color: 'white',
    border: 'none',
    padding: '8px 15px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500'
  },
  reorderButton: {
    background: '#4CAF50',
    color: 'white',
    border: 'none',
    padding: '8px 15px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500'
  },
  ordersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  orderItem: {
    display: 'flex',
    gap: '15px',
    padding: '20px',
    background: '#f8f9fa',
    borderRadius: '8px',
    borderLeft: '5px solid #d4af37'
  },
  orderMainInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  orderSecondaryInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  orderItems: {
    margin: 0,
    fontSize: '14px'
  },
  profileCard: {
    background: '#f8f9fa',
    padding: '30px',
    borderRadius: '12px',
    textAlign: 'center'
  },
  profileHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    marginBottom: '25px',
    justifyContent: 'center'
  },
  profileImage: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    objectFit: 'cover'
  },
  profileInitials: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'linear-gradient(45deg, #d4af37, #f4d03f)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    fontWeight: '600',
    color: '#1a1a1a'
  },
  profileInfo: {
    textAlign: 'left'
  },
  profileName: {
    fontSize: '24px',
    fontWeight: '700',
    margin: '0 0 5px 0',
    color: '#1a1a1a'
  },
  profileEmail: {
    fontSize: '16px',
    color: '#666',
    margin: '0 0 8px 0'
  },
  profileRole: {
    background: '#d4af37',
    color: '#1a1a1a',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600'
  },
  profileDetails: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '15px',
    marginBottom: '25px',
    textAlign: 'left'
  },
  detailItem: {
    fontSize: '14px',
    color: '#666'
  },
  profileActions: {
    display: 'flex',
    gap: '15px',
    justifyContent: 'center'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px'
  },
  emptyText: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '20px'
  },
  ctaButton: {
    background: 'linear-gradient(45deg, #d4af37, #f4d03f)',
    color: '#1a1a1a',
    border: 'none',
    padding: '12px 25px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600'
  },
  helpText: {
    fontSize: '12px',
    color: '#666',
    marginTop: '5px',
    display: 'block'
  },

  headerActions: {
    display: 'flex',
    gap: '15px',
    alignItems: 'center'
  },
  
  viewModeSelect: {
    padding: '8px 12px',
    border: '2px solid #e0e0e0',
    borderRadius: '6px',
    fontSize: '14px'
  },
  
  filtersContainer: {
    marginBottom: '20px',
    padding: '15px',
    background: '#f8f9fa',
    borderRadius: '8px'
  },
  
  filterRow: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    marginBottom: '10px'
  },
  
  filterInput: {
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    minWidth: '150px'
  },
  
  exportButtons: {
    display: 'flex',
    gap: '10px'
  },
  
  exportButton: {
    background: '#28a745',
    color: 'white',
    border: 'none',
    padding: '8px 15px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px'
  },
  
  tableContainer: {
    overflowX: 'auto',
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  
  tableHeader: {
    padding: '12px 15px',
    textAlign: 'left',
    backgroundColor: '#f8f9fa',
    borderBottom: '2px solid #dee2e6',
    fontWeight: '600',
    color: '#495057'
  },
  
  tableRow: {
    borderBottom: '1px solid #dee2e6',
    '&:hover': {
      backgroundColor: '#f8f9fa'
    }
  },
  
  tableCell: {
    padding: '12px 15px',
    verticalAlign: 'top'
  },
  
  tableImage: {
    width: '50px',
    height: '50px',
    objectFit: 'cover',
    borderRadius: '4px'
  },
  
  statusBadge: {
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'white'
  },
  
  roleBadge: {
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'white'
  },
  
  actionButtons: {
    display: 'flex',
    gap: '5px',
    flexWrap: 'wrap'
  },
  
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px',
    borderTop: '1px solid #dee2e6'
  },
  
  paginationInfo: {
    fontSize: '14px',
    color: '#666'
  },
  
  paginationControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  
  paginationButton: {
    padding: '8px 12px',
    border: '1px solid #ddd',
    background: 'white',
    borderRadius: '4px',
    cursor: 'pointer',
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  },
  
  paginationNumbers: {
    fontSize: '14px',
    fontWeight: '600'
  },
  
  pageSizeSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px'
  },
  
  pageSizeSelect: {
    padding: '4px 8px',
    border: '1px solid #ddd',
    borderRadius: '4px'
  }

};


// Authentication Styles
const authStyles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  },
  container: {
    width: '100%',
    maxWidth: '450px',
    margin: '0 auto'
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '40px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    border: '1px solid #eaeaea'
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px'
  },
  title: {
    fontSize: '28px',
    fontWeight: '800',
    color: '#1a1a1a',
    marginBottom: '8px',
    background: 'linear-gradient(45deg, #1a1a1a, #d4af37)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  },
  subtitle: {
    fontSize: '16px',
    color: '#666',
    margin: 0
  },
  form: {
    marginBottom: '25px'
  },
  formGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#1a1a1a',
    fontSize: '14px'
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '16px',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
    fontFamily: 'inherit'
  },
  phoneInputContainer: {
    display: 'flex',
    gap: '10px'
  },
  countryCodeSelect: {
    padding: '14px 12px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '16px',
    backgroundColor: 'white',
    minWidth: '120px',
    fontFamily: 'inherit'
  },
  phoneInput: {
    flex: 1,
    padding: '14px 16px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '16px',
    fontFamily: 'inherit'
  },
  checkboxGroup: {
    marginBottom: '25px'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    color: '#555',
    cursor: 'pointer'
  },
  checkbox: {
    marginRight: '8px'
  },
  submitButton: {
    width: '100%',
    background: 'linear-gradient(45deg, #1a1a1a, #2d2d2d)',
    color: 'white',
    border: 'none',
    padding: '16px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginBottom: '20px'
  },
  divider: {
    position: 'relative',
    textAlign: 'center',
    margin: '25px 0',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: 0,
      right: 0,
      height: '1px',
      background: '#e0e0e0'
    }
  },
  dividerText: {
    background: 'white',
    padding: '0 15px',
    color: '#666',
    fontSize: '14px'
  },
  googleSection: {
    marginBottom: '25px',
    display: 'flex',
    justifyContent: 'center'
  },
  footer: {
    textAlign: 'center'
  },
  footerText: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '15px'
  },
  linkButton: {
    background: 'none',
    border: 'none',
    color: '#d4af37',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontSize: 'inherit'
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: '#666',
    cursor: 'pointer',
    fontSize: '14px',
    textDecoration: 'underline'
  },
  loadingScreen: {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  loadingContent: {
    textAlign: 'center'
  },
  loadingSpinner: {
    width: '50px',
    height: '50px',
    border: '4px solid #e0e0e0',
    borderTop: '4px solid #d4af37',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px'
  },
  loadingText: {
    fontSize: '18px',
    color: '#666',
    fontWeight: '600'
  }
};

// Add keyframes for loading spinner
const spinKeyframes = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

// Inject the keyframes into the document
const styleSheet = document.styleSheets[0];
styleSheet.insertRule(spinKeyframes, styleSheet.cssRules.length);

// Your existing styles remain exactly the same...
const styles = {
  app: {
    fontFamily: "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    lineHeight: 1.6,
    color: '#2c3e50',
    margin: 0,
    padding: 0,
    backgroundColor: '#f8f9fa',
    overflowX: 'hidden',
    minHeight: '100vh'
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px',
    width: '100%',
    boxSizing: 'border-box'
  },
  
  // Header Styles
  header: {
    background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
    color: 'white',
    padding: '15px 0',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  },
  logoSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
  },
  logo: {
    fontSize: 'clamp(20px, 4vw, 28px)',
    fontWeight: '800',
    margin: 0,
    color: '#ffffff',
    letterSpacing: '1px',
    textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
  },
  menuToggle: {
    background: 'none',
    border: 'none',
    color: 'white',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '5px 10px'
  },
  nav: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center'
  },
  navList: {
    display: 'flex',
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  navItem: {
    margin: '0'
  },
  navLink: {
    color: 'white',
    textDecoration: 'none',
    fontWeight: '600',
    fontSize: 'clamp(14px, 2vw, 16px)',
    transition: 'all 0.3s ease',
    padding: '8px 12px',
    borderRadius: '6px',
    display: 'block',
    textAlign: 'center'
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px'
  },
  searchContainer: {
    marginLeft: '30px'
  },
  searchInput: {
    padding: '10px 15px',
    borderRadius: '25px',
    border: '1px solid #444',
    fontSize: '14px',
    backgroundColor: 'rgba(255,255,255,0.1)',
    color: 'white',
    width: '200px',
  },
  authSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  authButtons: {
    display: 'flex',
    gap: '10px'
  },
  loginButton: {
    background: 'transparent',
    border: '1px solid #d4af37',
    color: '#d4af37',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  signupButton: {
    background: '#d4af37',
    border: '1px solid #d4af37',
    color: '#1a1a1a',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  userMenu: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  userWelcome: {
    fontSize: '14px',
    color: '#d4af37',
    fontWeight: '600'
  },
  logoutButton: {
    background: 'transparent',
    border: '1px solid #ff4444',
    color: '#ff4444',
    padding: '6px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  cartIcon: {
    position: 'relative',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '50%',
    transition: 'background-color 0.3s ease'
  },
  cartBadge: {
    position: 'absolute',
    top: '-5px',
    right: '-5px',
    backgroundColor: '#d4af37',
    color: '#1a1a1a',
    borderRadius: '50%',
    width: '20px',
    height: '20px',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold'
  },
  
  // Hero Section
  hero: {
    backgroundImage: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.6)), url(https://images.unsplash.com/photo-1604503468506-a8da13d82791?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTJ8fHBvcmslMjBzaG91bGRlcnxlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=1400&q=60)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
    minHeight: '85vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    color: 'white',
    position: 'relative',
    padding: '20px'
  },
  heroOverlay: {
    padding: '40px 20px',
    maxWidth: '800px',
    width: '100%',
  },
  heroContent: {
    padding: '0 20px',
    animation: 'fadeInUp 1s ease-out'
  },
  heroTitle: {
    marginBottom: '20px',
    fontWeight: '800',
    textShadow: '2px 2px 8px rgba(0,0,0,0.7)',
    background: 'linear-gradient(45deg, #ffffff, #d4af37)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    lineHeight: '1.2'
  },
  heroSubtitle: {
    marginBottom: '40px',
    fontWeight: '300',
    opacity: 0.9,
    textShadow: '1px 1px 4px rgba(0,0,0,0.5)',
    lineHeight: '1.4'
  },
  ctaButton: {
    background: 'linear-gradient(45deg, #d4af37, #f4d03f)',
    color: '#1a1a1a',
    border: 'none',
    padding: '16px 40px',
    fontSize: '18px',
    borderRadius: '50px',
    cursor: 'pointer',
    fontWeight: '700',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 20px rgba(212, 175, 55, 0.4)',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    width: 'auto',
    minWidth: '200px'
  },
  
  // Section Title
  sectionTitle: {
    textAlign: 'center',
    marginBottom: '50px',
    color: '#1a1a1a',
    fontWeight: '800',
    position: 'relative',
    lineHeight: '1.2'
  },
  
  // About Section
  about: {
    padding: '60px 0',
    backgroundColor: '#ffffff',
    position: 'relative'
  },
  aboutContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '60px',
  },
  aboutText: {
    flex: 1,
  },
  aboutHeading: {
    fontSize: '24px',
    marginBottom: '20px',
    color: '#1a1a1a',
    fontWeight: '700'
  },
  aboutParagraph: {
    marginBottom: '25px',
    fontSize: '16px',
    lineHeight: '1.8',
    color: '#555'
  },
  infoBox: {
    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    padding: '25px',
    borderRadius: '12px',
    marginTop: '30px',
    borderLeft: '5px solid #d4af37',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)'
  },
  infoItem: {
    marginBottom: '20px'
  },
  infoTitle: {
    color: '#1a1a1a',
    marginBottom: '8px',
    fontSize: '18px',
    fontWeight: '700'
  },
  infoText: {
    color: '#555',
    margin: 0,
    fontSize: '15px',
    lineHeight: '1.5'
  },
  aboutImage: {
    flex: 1,
    textAlign: 'center',
    position: 'relative'
  },
  aboutImg: {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: '12px',
    boxShadow: '0 15px 30px rgba(0, 0, 0, 0.15)',
    transition: 'transform 0.3s ease',
  },
  
  // Products Section
  products: {
    padding: '60px 0',
    backgroundColor: '#f8f9fa',
    position: 'relative'
  },
  productsGrid: {
    display: 'grid',
    marginBottom: '50px',
  },
  productCard: {
    background: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease',
    textAlign: 'center',
    position: 'relative',
    height: '100%'
  },
  productImage: {
    height: '200px',
    overflow: 'hidden',
    position: 'relative'
  },
  productImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.5s ease',
  },
  productName: {
    padding: '20px 20px 10px',
    color: '#1a1a1a',
    margin: 0,
    fontSize: '20px',
    fontWeight: '700'
  },
  productDescription: {
    padding: '0 20px 20px',
    color: '#666',
    margin: 0,
    fontSize: '15px',
    lineHeight: '1.6'
  },
  
  // Why Choose Us Section
  whyChooseUs: {
    padding: '60px 0',
    backgroundColor: '#ffffff',
    position: 'relative'
  },
  benefitsGrid: {
    display: 'grid',
    marginBottom: '50px',
  },
  benefitCard: {
    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    padding: '30px',
    borderRadius: '12px',
    textAlign: 'center',
    borderTop: '5px solid #d4af37',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s ease',
    height: '100%'
  },
  benefitTitle: {
    color: '#1a1a1a',
    marginBottom: '15px',
    fontSize: '20px',
    fontWeight: '700'
  },
  benefitText: {
    color: '#555',
    margin: 0,
    lineHeight: '1.7',
    fontSize: '15px'
  },
  qualitySlogan: {
    textAlign: 'center',
    padding: '40px',
    background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
    borderRadius: '15px',
    marginTop: '30px',
    boxShadow: '0 10px 30px rgba(212, 175, 55, 0.3)'
  },
  sloganText: {
    color: '#1a1a1a',
    fontStyle: 'italic',
    margin: 0,
    fontSize: '24px',
    fontWeight: '700',
    textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
  },
  
  // Testimonials Section
  testimonials: {
    padding: '60px 0',
    backgroundColor: '#f8f9fa',
    position: 'relative'
  },
  testimonialsGrid: {
    display: 'grid',
  },
  testimonialCard: {
    background: 'white',
    padding: '30px',
    borderRadius: '12px',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
    position: 'relative',
    transition: 'all 0.3s ease',
    height: '100%'
  },
  testimonialText: {
    marginBottom: '20px',
    position: 'relative'
  },
  testimonialQuote: {
    fontStyle: 'italic',
    margin: 0,
    color: '#555',
    lineHeight: '1.7',
    fontSize: '15px',
    position: 'relative',
    paddingLeft: '25px',
  },
  customerInfo: {
    borderTop: '2px solid #f0f0f0',
    paddingTop: '15px',
  },
  customerName: {
    color: '#1a1a1a',
    marginBottom: '5px',
    margin: 0,
    fontWeight: '700',
    fontSize: '16px'
  },
  customerRole: {
    color: '#d4af37',
    fontSize: '14px',
    margin: 0,
    fontWeight: '600'
  },
  
  // Contact Section
  contact: {
    padding: '60px 0',
    backgroundColor: '#ffffff',
    position: 'relative'
  },
  contactContent: {
    display: 'flex',
    gap: '60px',
  },
  contactInfo: {
    flex: 1,
  },
  contactForm: {
    flex: 1,
  },
  contactHeading: {
    color: '#1a1a1a',
    marginBottom: '25px',
    fontSize: '24px',
    fontWeight: '700'
  },
  contactSubHeading: {
    color: '#1a1a1a',
    marginBottom: '10px',
    fontSize: '18px',
    fontWeight: '700'
  },
  contactText: {
    marginBottom: '20px',
    color: '#555',
    fontSize: '15px',
    lineHeight: '1.6'
  },
  contactItem: {
    marginBottom: '25px',
    padding: '20px',
    background: '#f8f9fa',
    borderRadius: '8px',
    borderLeft: '4px solid #d4af37'
  },
  socialSection: {
    marginTop: '30px'
  },
  socialLinks: {
    display: 'flex',
    gap: '15px'
  },
  socialLink: {
    color: '#d4af37',
    textDecoration: 'none',
    fontWeight: '600',
    fontSize: '15px',
    padding: '8px 16px',
    border: '2px solid #d4af37',
    borderRadius: '25px',
    transition: 'all 0.3s ease',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  formGroup: {
    marginBottom: '20px',
  },
  formLabel: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#1a1a1a',
    fontSize: '15px'
  },
  formInput: {
    width: '100%',
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontFamily: 'inherit',
    fontSize: '15px',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box'
  },
  phoneInputContainer: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center'
  },
  countryCodeSelect: {
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontFamily: 'inherit',
    fontSize: '15px',
    backgroundColor: 'white',
    minWidth: '120px'
  },
  phoneInput: {
    flex: 1,
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontFamily: 'inherit',
    fontSize: '15px',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box'
  },
  formTextarea: {
    width: '100%',
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontFamily: 'inherit',
    fontSize: '15px',
    resize: 'vertical',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box'
  },
  submitButton: {
    background: 'linear-gradient(45deg, #1a1a1a, #2d2d2d)',
    color: 'white',
    border: 'none',
    padding: '14px 30px',
    fontSize: '16px',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontWeight: '700',
    marginTop: '10px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    width: '100%'
  },
  
  // Footer
  footer: {
    background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
    color: 'white',
    padding: '50px 0 20px',
    position: 'relative'
  },
  footerContent: {
    display: 'grid',
    marginBottom: '40px',
  },
  footerSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  footerLogo: {
    color: '#ffffff',
    marginBottom: '15px',
    fontSize: '22px',
    fontWeight: '800'
  },
  footerTagline: {
    color: '#d4af37',
    marginBottom: '10px',
    fontWeight: '600',
    fontSize: '16px'
  },
  footerText: {
    marginBottom: '10px',
    color: '#ccc',
    fontSize: '14px',
    lineHeight: '1.6'
  },
  footerHeading: {
    color: '#d4af37',
    marginBottom: '20px',
    fontSize: '18px',
    fontWeight: '700'
  },
  footerList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  footerListItem: {
    marginBottom: '8px',
  },
  footerLink: {
    color: '#ccc',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    fontSize: '14px',
  },
  footerBottom: {
    textAlign: 'center',
    paddingTop: '25px',
    borderTop: '1px solid #444',
    color: '#999',
  },
  footerBottomText: {
    margin: 0,
    fontSize: '14px'
  },
};

// Shop Page Styles
const shopStyles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa'
  },
  hero: {
    background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
    color: 'white',
    padding: '80px 20px',
    textAlign: 'center'
  },
  heroContent: {
    maxWidth: '800px',
    margin: '0 auto'
  },
  heroTitle: {
    fontSize: '48px',
    fontWeight: '800',
    marginBottom: '20px',
    background: 'linear-gradient(45deg, #ffffff, #d4af37)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  },
  heroSubtitle: {
    fontSize: '20px',
    opacity: 0.9,
    fontWeight: '300'
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '40px 20px'
  },
  filters: {
    display: 'flex',
    gap: '20px',
    marginBottom: '40px',
    flexWrap: 'wrap'
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  filterLabel: {
    fontWeight: '600',
    color: '#1a1a1a'
  },
  filterSelect: {
    padding: '10px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    backgroundColor: 'white',
    fontSize: '14px',
    minWidth: '150px'
  },
  productsGrid: {
    display: 'grid',
    gap: '30px'
  },
  productCard: {
    background: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease'
  },
  productImage: {
    position: 'relative',
    height: '250px',
    overflow: 'hidden'
  },
  productImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s ease'
  },
  productCategory: {
    position: 'absolute',
    top: '15px',
    left: '15px',
    background: '#d4af37',
    color: '#1a1a1a',
    padding: '5px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600'
  },
  wholesaleBadge: {
    position: 'absolute',
    top: '15px',
    right: '15px',
    background: '#2d5016',
    color: 'white',
    padding: '5px 10px',
    borderRadius: '20px',
    fontSize: '10px',
    fontWeight: 'bold',
    textTransform: 'uppercase'
  },
  productInfo: {
    padding: '25px'
  },
  productName: {
    fontSize: '22px',
    fontWeight: '700',
    marginBottom: '10px',
    color: '#1a1a1a'
  },
  productDescription: {
    color: '#666',
    marginBottom: '20px',
    lineHeight: '1.6'
  },
  productDetails: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  productWeight: {
    background: '#f8f9fa',
    padding: '5px 12px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#666'
  },
  productPrice: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#d4af37'
  },
  purchaseOptions: {
    marginBottom: '20px',
    padding: '15px',
    background: '#f8f9fa',
    borderRadius: '8px'
  },
  purchaseTypeSelector: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '15px'
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  radioInput: {
    margin: 0
  },
  quantitySelector: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  quantityLabel: {
    fontSize: '14px',
    fontWeight: '600'
  },
  quantityControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  quantityButton: {
    width: '30px',
    height: '30px',
    border: '1px solid #ddd',
    background: 'white',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px'
  },
  quantityInput: {
    width: '60px',
    padding: '5px',
    textAlign: 'center',
    border: '1px solid #ddd',
    borderRadius: '4px'
  },
  customInput: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  inputLabel: {
    fontSize: '14px',
    fontWeight: '600'
  },
  textInput: {
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px'
  },
  calculation: {
    fontSize: '14px',
    color: '#666',
    fontStyle: 'italic'
  },
  addToCartButton: {
    width: '100%',
    background: 'linear-gradient(45deg, #1a1a1a, #2d2d2d)',
    color: 'white',
    border: 'none',
    padding: '15px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  }
};

// Checkout Page Styles
const checkoutStyles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa'
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '40px 20px'
  },
  title: {
    fontSize: '36px',
    fontWeight: '800',
    marginBottom: '40px',
    color: '#1a1a1a',
    textAlign: 'center'
  },
  authPrompt: {
    background: 'linear-gradient(45deg, #fff3cd, #ffeaa7)',
    padding: '15px 20px',
    borderRadius: '8px',
    marginBottom: '30px',
    textAlign: 'center',
    border: '1px solid #ffeaa7'
  },
  authLink: {
    background: 'none',
    border: 'none',
    color: '#d4af37',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontWeight: '600',
    margin: '0 5px'
  },
  content: {
    display: 'flex',
    gap: '40px'
  },
  orderSummary: {
    flex: 1,
    background: 'white',
    borderRadius: '12px',
    padding: '30px',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)'
  },
  sectionTitle: {
    fontSize: '24px',
    fontWeight: '700',
    marginBottom: '25px',
    color: '#1a1a1a',
    paddingBottom: '15px',
    borderBottom: '2px solid #f0f0f0'
  },
  emptyCart: {
    textAlign: 'center',
    padding: '40px'
  },
  shopButton: {
    background: 'linear-gradient(45deg, #d4af37, #f4d03f)',
    color: '#1a1a1a',
    border: 'none',
    padding: '12px 30px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '20px'
  },
  cartItems: {
    space: '20px'
  },
  cartItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    padding: '20px 0',
    borderBottom: '1px solid #f0f0f0'
  },
  cartItemImage: {
    width: '80px',
    height: '80px',
    objectFit: 'cover',
    borderRadius: '8px'
  },
  cartItemDetails: {
    flex: 1
  },
  cartItemName: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '5px',
    color: '#1a1a1a'
  },
  cartItemPrice: {
    color: '#d4af37',
    fontWeight: '600',
    fontSize: '14px'
  },
  cartItemWeight: {
    color: '#666',
    fontSize: '12px',
    marginTop: '2px'
  },
  quantityControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  quantityButton: {
    width: '30px',
    height: '30px',
    border: '1px solid #ddd',
    background: 'white',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  quantity: {
    minWidth: '30px',
    textAlign: 'center',
    fontWeight: '600'
  },
  cartItemTotal: {
    fontWeight: '700',
    color: '#1a1a1a',
    minWidth: '80px',
    textAlign: 'right'
  },
  removeButton: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#ff4444',
    padding: '5px'
  },
  orderTotal: {
    marginTop: '30px',
    paddingTop: '20px',
    borderTop: '2px solid #f0f0f0'
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '10px',
    fontSize: '16px'
  },
  grandTotal: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1a1a1a'
  },
  customerInfo: {
    flex: 1,
    background: 'white',
    borderRadius: '12px',
    padding: '30px',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column'
  },
  label: {
    fontWeight: '600',
    marginBottom: '8px',
    color: '#1a1a1a'
  },
  input: {
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '16px',
    transition: 'border-color 0.3s ease'
  },
  phoneInputContainer: {
    display: 'flex',
    gap: '10px'
  },
  countryCodeSelect: {
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '16px',
    backgroundColor: 'white',
    minWidth: '120px'
  },
  phoneInput: {
    flex: 1,
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '16px'
  },
  textarea: {
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '16px',
    resize: 'vertical',
    minHeight: '80px',
    fontFamily: 'inherit'
  },
  select: {
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '16px',
    background: 'white'
  },
  deliveryOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  radioInput: {
    margin: 0
  },
  bankDetails: {
    background: '#f8f9fa',
    padding: '15px',
    borderRadius: '8px',
    borderLeft: '4px solid #d4af37'
  },
  bankTitle: {
    margin: '0 0 10px 0',
    color: '#1a1a1a',
    fontSize: '16px'
  },
  bankInfo: {
    margin: '0 0 10px 0',
    fontSize: '14px',
    lineHeight: '1.5'
  },
  bankNote: {
    margin: 0,
    fontSize: '12px',
    color: '#666',
    fontStyle: 'italic'
  },
  submitButton: {
    background: 'linear-gradient(45deg, #d4af37, #f4d03f)',
    color: '#1a1a1a',
    border: 'none',
    padding: '18px',
    borderRadius: '8px',
    fontSize: '18px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginTop: '10px'
  },
  successMessage: {
    textAlign: 'center',
    background: 'white',
    padding: '60px 40px',
    borderRadius: '12px',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
    maxWidth: '600px',
    margin: '0 auto'
  },
  successIcon: {
    fontSize: '80px',
    color: '#4CAF50',
    marginBottom: '30px'
  },
  successTitle: {
    fontSize: '32px',
    fontWeight: '700',
    marginBottom: '20px',
    color: '#1a1a1a'
  },
  successText: {
    fontSize: '18px',
    color: '#666',
    marginBottom: '15px',
    lineHeight: '1.6'
  },
  successDetails: {
    fontSize: '16px',
    color: '#888',
    marginBottom: '30px'
  },
  continueShopping: {
    background: 'linear-gradient(45deg, #1a1a1a, #2d2d2d)',
    color: 'white',
    border: 'none',
    padding: '15px 30px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer'
  }
};

export default App;